import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { throttle } from "lodash";
import { ISidebarItem } from "../../../../components/common/sidebar/SidebarItem";
import { ArrowUpDown, ChevronDown, Info, Network } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  createWorkflow,
  fetchGenerateAdHistory,
  fetchWorkflowById,
  fetchWorkflowsList,
  generateAd,
  retryWorkflow,
  updateCreative,
} from "../../apiService";
import { uploadFilesAsync } from "@/api/globalApiService";
import { useParams, useSearchParams } from "react-router-dom";
import {
  AdCreative,
  CreativeWorkflow,
  GenerateAdData,
  Moodboard,
  VideoType,
  WorkflowCreativePrompt,
} from "../../types";
import { File as CustomFile } from "@/types";
import { LuInfo } from "react-icons/lu";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { useCreativeLibraryStore } from "../../store";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import BookmarkAction from "../adActions/BookmarkAction";
import ReportTitle from "@/screens/analysis/components/report/ReportTitle";
import { useGlobalStore } from "@/GlobalStore";
import { FileAttachmentsPreview } from "@/components/hoc/fileAttachment";
import { UploadingFile } from "@/components/hoc/fileAttachment/types";
import EditImageAction from "../adActions/EditImageAction";
import EditImageDialog from "../dialog/EditImageDialog";
import EditImageDialogV2 from "../dialog/EditImageDialogV2";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Action } from "@/components/common/adCard/types";
import { PollingAdCard } from "@/components/common/adCard";
import ImageSwitcherAction from "@/components/common/adCard/actions/ImageSwitcherAction";
import { LibraryAttachmentsPreview } from "@/components/hoc/mediaLibrary";
import { LibraryMedia } from "@/types";
// tooltips removed in favor of a single plus dropdown
import PageLoader from "@/components/common/loaders/PageLoader";
import ItemLoader from "@/components/common/loaders/ItemLoader";
import { RiBrushAiLine } from "@remixicon/react";
import {
  RiApps2AiLine,
  RiArtboardLine,
  RiAspectRatioLine,
  RiCalendarLine,
  RiChatSmileAiLine,
  RiCheckLine,
  RiEditLine,
  RiHome3Line,
  RiImage2Line,
  RiLandscapeAiLine,
  RiMapPinLine,
  RiMegaphoneLine,
  RiSortAlphabetDesc,
  RiVideoLine,
} from "@remixicon/react";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { AspectRatio } from "./AspectRatioDropdown";
import { Variations } from "./VariationsDropdown";
import { coerceModel, Model } from "./ModelDropdown";
import { Image } from "@/screens/creatives/types";
import CreativeInputSection from "../common/CreativeInputSection";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  CREATIVE_LIBRARY_STORAGE_KEYS,
} from "@/utils/localStorage";
import Copilot from "@/components/common/copilot";
import MoodboardManagerDialog from "../dialog/MoodboardManagerDialog";
import { useMoodboardsManager } from "../../hooks/useMoodboardsManager";

const ATTACHMENT_LIMIT = 5;

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "video/mp4",
  "video/webm",
] as const;

export const CreateTab: ISidebarItem = {
  type: "tab",
  value: "create",
  label: "Create",
  icon: RiBrushAiLine,
  id: "create",
  route: "",
};
type SortOrder = "newest-first" | "oldest-first";
type CreateMode = "generate_ads" | "workflow";
type WorkflowsListResponse = {
  status: string;
  data: CreativeWorkflow[];
  pagination: {
    page_num: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
};
type WorkflowByIdResponse = {
  status: string;
  data: {
    workflow: CreativeWorkflow;
  };
};
type CreateWorkflowResponse = {
  status: string;
  data: {
    workflow: CreativeWorkflow;
  };
};
type RetryWorkflowResponse = {
  status: string;
  data?: {
    workflow: CreativeWorkflow;
  };
};

interface PromptCopyData {
  prompt: string;
  files: CustomFile[];
  libraryFiles: CustomFile[];
}

const PromptCopyButton = ({ data, onCopy }: { data: PromptCopyData; onCopy?: (data: PromptCopyData) => void }) => {
  const [state, setState] = useState<"idle" | "copied">("idle");
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    onCopy?.(data);
    setState("copied");
    setTimeout(() => setState("idle"), 1500);
  };

  const icon =
    state === "copied" ? (
      <RiCheckLine size={14} className="shrink-0" color="#FF4500" />
    ) : hovered ? (
      <RiEditLine size={14} className="shrink-0" color="#FF4500" />
    ) : (
      <RiArtboardLine size={14} className="shrink-0" color="#9AA3BD" />
    );

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="bg-[#fff] rounded-[8px] p-1 flex items-center justify-center h-8 w-8 flex-shrink-0 border-[1px] border-border-gray shadow-sm cursor-pointer transition-colors hover:border-[#FF450033]"
    >
      {icon}
    </button>
  );
};

// RenderMsg component - placed before VirtualizedMessagesList which uses it
// Memoized to prevent unnecessary re-renders when parent state changes
const RenderMsg = memo(
  ({
    msg,
    msgIndex,
    handleBookmark,
    onCreativeUpdate,
    createdAdsMessages,
    setCreatedAdsMessages,
    handleEditDialog,
    handleEditDialogV2,
    currentImageIndexByCreativeId,
    setCurrentImageIndexByCreativeId,
    onCopyPrompt,
  }: {
    msg: GenerateAdData;
    msgIndex: number;
    handleBookmark: (folderId: string | null, msgId: string, creativeId: string) => void;
    onCreativeUpdate: (updated: AdCreative) => void;
    createdAdsMessages: GenerateAdData[];
    setCreatedAdsMessages: React.Dispatch<React.SetStateAction<GenerateAdData[]>>;
    sortOrder: "newest-first" | "oldest-first";
    handleEditDialog: (open: boolean, image: Image, adCreative: AdCreative) => void;
    handleEditDialogV2: (open: boolean, ad: AdCreative) => void;
    currentImageIndexByCreativeId: Record<string, number>;
    setCurrentImageIndexByCreativeId: React.Dispatch<
      React.SetStateAction<Record<string, number>>
    >;
    onCopyPrompt?: (data: PromptCopyData) => void;
  }) => {
    const failed = msg.ad_creatives.some((adCreative) => adCreative.status === "failed");
    const allFailed = msg.ad_creatives.every(
      (adCreative) => adCreative.status === "failed",
    );
    const { selectedAccount } = useGlobalStore();

    // Memoize the poll complete callback to prevent unnecessary re-renders
    const onSinglePollComplete = useCallback(
      (adCreative: AdCreative) => {
        setCreatedAdsMessages((prevMessages) =>
          prevMessages.map((message) => ({
            ...message,
            ad_creatives:
              message.id === msg.id
                ? message.ad_creatives.map((ad) =>
                    ad.id === adCreative.id ? adCreative : ad,
                  )
                : message.ad_creatives,
          })),
        );
      },
      [msg.id, setCreatedAdsMessages],
    );

    return (
      <div className="flex flex-col w-full">
        {/* User Input */}
        <div className="w-full flex gap-3">
          <PromptCopyButton data={{ prompt: msg.prompt, files: msg.files, libraryFiles: msg.library_files }} onCopy={onCopyPrompt} />
          <div className="flex flex-col gap-2 p-0 w-full overflow-hidden pt-1">
            <span className="font-normal text-[14px] text-brand-primary-800 break-words whitespace-pre-wrap">
              {msg.prompt}
            </span>
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto scrollbar-hide py-2">
              {msg.files.length > 0 && (
                <FileAttachmentsPreview
                  files={msg.files}
                  className="!overflow-visible py-0 flex-none"
                />
              )}
              {msg.files.length > 0 && msg.library_files.length > 0 && (
                <Separator
                  orientation="vertical"
                  className="h-[75px] bg-[#D4D4D4] shrink-0 mx-1"
                />
              )}
              {msg.library_files.length > 0 && (
                <LibraryAttachmentsPreview
                  attachments={msg.library_files}
                  className="!overflow-visible py-0 flex-none"
                />
              )}
            </div>
          </div>
        </div>

        {/* Response Ad Previews */}
        <div
          className={`flex gap-2 ${allFailed ? "py-2" : "py-4"} overflow-x-auto scrollbar-hide`}
        >
          {msg.ad_creatives.map((adCreative: AdCreative) => {
            const availableImages = adCreative.creative?.images || [];
            const hasMultipleImages = availableImages.length > 1;
            const currentImageIndex = currentImageIndexByCreativeId[adCreative.id] || 0;
            const currentImage = availableImages[currentImageIndex];

            const actions: Action[] = [
              {
                component: (
                  <DownloadAction
                    url={
                      adCreative.creative_type === "video"
                        ? adCreative.creative?.videos?.[0]?.permalink_url
                        : currentImage?.uncompressed_url || currentImage?.permalink_url
                    }
                    type={adCreative.creative_type}
                    aspectRatio={
                      adCreative.creative_type === "video"
                        ? adCreative.creative?.videos?.[0]?.aspect_ratio
                        : currentImage?.aspect_ratio
                    }
                  />
                ),
                position: "top-right",
                visibility: "hover",
              },
              {
                component: (
                  <BookmarkAction
                    folderId={adCreative.folder_id}
                    onBookmark={(folderId) => {
                      handleBookmark(folderId, msg.id, adCreative.id);
                    }}
                  />
                ),
                position: "top-right",
                visibility: "always",
              },
              ...(adCreative.creative_type === "image" && hasMultipleImages
                ? [
                    {
                      component: (
                        <ImageSwitcherAction
                          currentIndex={currentImageIndex}
                          totalImages={availableImages.length}
                          onPrevious={() => {
                            if (currentImageIndex > 0) {
                              setCurrentImageIndexByCreativeId((prev) => ({
                                ...prev,
                                [adCreative.id]: currentImageIndex - 1,
                              }));
                            }
                          }}
                          onNext={() => {
                            if (currentImageIndex < availableImages.length - 1) {
                              setCurrentImageIndexByCreativeId((prev) => ({
                                ...prev,
                                [adCreative.id]: currentImageIndex + 1,
                              }));
                            }
                          }}
                        />
                      ),
                      position: "top-left" as const,
                      visibility: "always-hover" as const,
                    },
                  ]
                : []),
              ...(adCreative.creative_type === "image"
                ? [
                    {
                      component: (
                        <EditImageAction
                          onClick={() => handleEditDialogV2(true, adCreative)}
                        />
                      ),
                      position: "bottom-right" as const,
                      visibility: "always-hover" as const,
                    },
                  ]
                : []),
            ];
            return (
              <div key={adCreative.id} className="max-w-[296px] flex-shrink-0">
                <PollingAdCard
                  accountIconUrl={selectedAccount?.additional_info?.ad_account_icon_url}
                  className="min-w-[296px] flex-shrink-0 p-2 rounded-[16px]"
                  adCreative={adCreative}
                  onCreativeUpdate={onCreativeUpdate}
                  mediaActions={actions}
                  onMediaClick={
                    adCreative.creative_type === "image"
                      ? () => handleEditDialog(true, currentImage, adCreative)
                      : undefined
                  }
                  onPollingComplete={onSinglePollComplete}
                  calculateHeight={true}
                  currentImageIndex={currentImageIndex}
                />
              </div>
            );
          })}
        </div>
        {failed && (
          <div className="flex gap-2 border border-solid border-[#FADDDA] bg-[#FDF6F5] rounded-[6px] p-2">
            <span className="p-3 bg-[#E54C4214] rounded-[4px] h-6 w-6 flex items-center justify-center">
              <Info size={16} className="shrink-0" color="#E54C42" />
            </span>
            <p className="text-[#E7574E] font-normal text-[14px] leading-[18px] tracking-[0.02em] w-[72%]">
              It looks like there was an issue with generating the image. Please check
              your connection and try again. If the problem continues, feel free to reach
              out to Getcrux team.
            </p>
          </div>
        )}
        <div className="text-brand-primary-400 text-[12px] font-normal leading-[18px] tracking-[0.02em] text-right self-end mt-4">
          {(() => {
            const date = new Date(msg.created_at);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const day = date.getDate();
            const month = date.toLocaleString("en-US", { month: "long" });
            const year = date.getFullYear();
            return `${hours % 12 || 12}:${minutes.toString().padStart(2, "0")}${hours >= 12 ? "PM" : "AM"}, ${day} ${month} ${year}`;
          })()}
        </div>

        {msgIndex !== createdAdsMessages.length - 1 && <Separator className="mt-6" />}
      </div>
    );
  },
);

// Display name for debugging
RenderMsg.displayName = "RenderMsg";

// Virtualized Messages List Component
interface VirtualizedMessagesListProps {
  messages: GenerateAdData[];
  containerRef: React.RefObject<HTMLDivElement>;
  handleBookmark: (folderId: string | null, msgId: string, creativeId: string) => void;
  onCreativeUpdate: (updated: AdCreative) => void;
  setCreatedAdsMessages: React.Dispatch<React.SetStateAction<GenerateAdData[]>>;
  sortOrder: "newest-first" | "oldest-first";
  handleEditDialog: (open: boolean, image: Image, adCreative: AdCreative) => void;
  handleEditDialogV2: (open: boolean, ad: AdCreative) => void;
  currentImageIndexByCreativeId: Record<string, number>;
  setCurrentImageIndexByCreativeId: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  loadingMore: boolean;
  hasNext: boolean;
  onLoadMore: () => void;
  onCopyPrompt?: (data: PromptCopyData) => void;
}

const VirtualizedMessagesList = memo(
  ({
    messages,
    containerRef,
    handleBookmark,
    onCreativeUpdate,
    setCreatedAdsMessages,
    sortOrder,
    handleEditDialog,
    handleEditDialogV2,
    currentImageIndexByCreativeId,
    setCurrentImageIndexByCreativeId,
    onCopyPrompt,
    loadingMore,
    hasNext,
    onLoadMore,
  }: VirtualizedMessagesListProps) => {
    const loadMoreTriggeredForCountRef = useRef<number | null>(null);

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
      count: messages.length,
      estimateSize: () => 400, // estimate message height
      getScrollElement: () => containerRef.current,
      measureElement:
        typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
          ? (element) => element?.getBoundingClientRect().height
          : undefined,
      overscan: 3,
      gap: 24, // 24px gap between messages (gap-6)
    });

    const virtualItems = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();

    // Reset the trigger ref when new data is loaded
    useEffect(() => {
      if (!loadingMore && loadMoreTriggeredForCountRef.current !== null) {
        if (messages.length > loadMoreTriggeredForCountRef.current) {
          loadMoreTriggeredForCountRef.current = null;
        }
      }
    }, [loadingMore, messages.length]);

    // Check for pagination when virtualizer scrolls near the end
    useEffect(() => {
      const lastItem = virtualItems[virtualItems.length - 1];
      if (!lastItem) return;

      const shouldLoadMore =
        lastItem.index >= messages.length - 3 &&
        hasNext &&
        !loadingMore &&
        loadMoreTriggeredForCountRef.current !== messages.length;

      if (shouldLoadMore) {
        loadMoreTriggeredForCountRef.current = messages.length;
        onLoadMore();
      }
    }, [virtualItems, messages.length, hasNext, loadingMore, onLoadMore]);

    return (
      <div className="w-full pb-[72px]">
        <div
          style={{
            height: `${totalSize}px`,
            position: "relative",
          }}
        >
          {virtualItems.map((virtualRow) => {
            const msg = messages[virtualRow.index];
            return (
              <div
                key={msg._listKey ?? msg.id}
                data-index={virtualRow.index}
                ref={(node) => rowVirtualizer.measureElement(node)}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <RenderMsg
                  msg={msg}
                  msgIndex={virtualRow.index}
                  handleBookmark={handleBookmark}
                  onCreativeUpdate={onCreativeUpdate}
                  createdAdsMessages={messages}
                  setCreatedAdsMessages={setCreatedAdsMessages}
                  sortOrder={sortOrder}
                  handleEditDialog={handleEditDialog}
                  handleEditDialogV2={handleEditDialogV2}
                  currentImageIndexByCreativeId={currentImageIndexByCreativeId}
                  setCurrentImageIndexByCreativeId={setCurrentImageIndexByCreativeId}
                  onCopyPrompt={onCopyPrompt}
                />
              </div>
            );
          })}
        </div>

        {/* Render loader in normal flow so it contributes to scroll height and stays visible */}
        {loadingMore && (
          <div className="flex justify-center py-4">
            <ItemLoader />
          </div>
        )}
      </div>
    );
  },
);

// Display name for debugging
VirtualizedMessagesList.displayName = "VirtualizedMessagesList";

const TITLE_HEIGHT = 56;
type CreateDashboardProps = {
  tabItem?: ISidebarItem;
  defaultCreateMode?: CreateMode;
  showAskAI?: boolean;
};

const CreateDashboard = ({
  tabItem = CreateTab,
  defaultCreateMode = "generate_ads",
  showAskAI = true,
}: CreateDashboardProps) => {
  const { selectedSidebarItem, setSelectedSidebarItem } = useCreativeLibraryStore();
  const { sidebarCollapsed, selectedAccount } = useGlobalStore();
  const userName = useGlobalStore((state) => state.userName);
  const [searchParams, setSearchParams] = useSearchParams();

  // setting initial tab
  useEffect(() => {
    if (selectedSidebarItem?.id !== tabItem.id) setSelectedSidebarItem(tabItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set aspect ratio based on selected account's ad_information only if no localStorage value exists
  useEffect(() => {
    // Check if user has a saved preference in localStorage
    const savedAspectRatio = localStorage.getItem(
      CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO,
    );
    const savedAvoidDangerZone = localStorage.getItem(
      CREATIVE_LIBRARY_STORAGE_KEYS.AVOID_DANGER_ZONE,
    );

    // Only apply account settings if no user preference exists
    if (!savedAspectRatio) {
      const accountAspectRatio =
        selectedAccount?.additional_info?.ad_information?.aspect_ratio;
      if (accountAspectRatio) {
        // Validate that the aspect ratio from account is one of the valid options
        const validAspectRatios: AspectRatio[] = [
          "Auto",
          "9:16",
          "2:3",
          "4:5",
          "1:1",
          "3:2",
          "16:9",
        ];
        if (validAspectRatios.includes(accountAspectRatio as AspectRatio)) {
          setAspectRatio(accountAspectRatio as AspectRatio);
        }
      } else {
        setAspectRatio("Auto");
      }
    }

    if (!savedAvoidDangerZone) {
      const accountAvoidDangerZone =
        selectedAccount?.additional_info?.ad_information?.avoid_danger_zone;
      if (accountAvoidDangerZone !== undefined) {
        setAvoidDangerZone(accountAvoidDangerZone);
      } else {
        setAvoidDangerZone(false);
      }
    }
    // This effect is intentionally keyed on account id only; we don't want it to re-run
    // for nested object identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id]);
  const { accountId } = useParams<{ accountId?: string }>();
  const [createMode, setCreateMode] = useState<CreateMode>(() => defaultCreateMode);
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [createdAdsMessages, setCreatedAdsMessages] = useState<GenerateAdData[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(true);
  const [workflowHistory, setWorkflowHistory] = useState<CreativeWorkflow[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState<boolean>(true);
  const contentAreaRef = useRef<HTMLDivElement>(null);

  const [sortOrder, setSortOrder] = useState<SortOrder>("newest-first");
  // Pagination helpers – handled by backend
  const pageSize = 10;
  const [page, setPage] = useState<number>(1);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [workflowPage, setWorkflowPage] = useState<number>(1);
  const [workflowHasNext, setWorkflowHasNext] = useState<boolean>(false);
  const [workflowLoadingMore, setWorkflowLoadingMore] = useState<boolean>(false);

  // The repo's axios typing makes some apiService functions appear as `any` at call sites.
  // Provide local, strongly-typed wrappers to satisfy eslint `no-unsafe-call`.
  const fetchWorkflowsListTyped = fetchWorkflowsList as unknown as (
    accountId: string,
    page_num: number,
    page_size: number,
    sort_type: "newest" | "oldest",
  ) => Promise<WorkflowsListResponse>;
  const createWorkflowTyped = createWorkflow as unknown as (
    accountId: string,
    data: {
      workflow_name: string;
      prompt: string;
      library_file_ids: string[];
      file_ids: string[];
    },
  ) => Promise<CreateWorkflowResponse>;
  const fetchWorkflowByIdTyped = fetchWorkflowById as unknown as (
    accountId: string,
    workflowId: string,
    options?: { signal?: AbortSignal },
  ) => Promise<WorkflowByIdResponse>;
  const retryWorkflowTyped = retryWorkflow as unknown as (
    accountId: string,
    workflowId: string,
  ) => Promise<RetryWorkflowResponse>;
  // Motion value controlling header translate-Y (negative → hidden)
  const headerOffset = useMotionValue(0);
  // Derived height & opacity so layout space collapses
  const headerHeight = useTransform(headerOffset, [-TITLE_HEIGHT, 0], [0, TITLE_HEIGHT]);
  const headerOpacity = useTransform(headerOffset, [-TITLE_HEIGHT, 0], [0, 1]);

  const handleSort = (order: SortOrder) => {
    if (order === sortOrder) return;
    setSortOrder(order);
    setPage(1);
    setHasNext(false);
    setCreatedAdsMessages([]);
    setWorkflowPage(1);
    setWorkflowHasNext(false);
    setWorkflowHistory([]);
    setTimeout(() => {
      if (contentAreaRef.current) {
        contentAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 0);
  };

  const displayText =
    sortOrder === "newest-first" ? "Latest to Oldest" : "Oldest to Latest";

  // Keep internal mode in sync with the route/tab that is rendering this dashboard
  useEffect(() => {
    setCreateMode(defaultCreateMode);
    // Reset lists so the next fetch starts from page 1 for the new mode
    setPage(1);
    setHasNext(false);
    setCreatedAdsMessages([]);
    setWorkflowPage(1);
    setWorkflowHasNext(false);
    setWorkflowHistory([]);
    // Scroll to top after switching mode
    setTimeout(() => {
      if (contentAreaRef.current) {
        contentAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 0);
  }, [defaultCreateMode]);

  const [prompt, setPrompt] = useState<string>("");
  const [files, setFiles] = useState<CustomFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
  const [creativeType, setCreativeType] = useState<"image" | "video">(() => {
    const storedType = loadFromLocalStorage(
      CREATIVE_LIBRARY_STORAGE_KEYS.CREATIVE_TYPE,
      "image",
    ) as "image" | "video";
    return storedType;
  });
  useEffect(() => {
    const typeParam = searchParams.get("type");
    if (typeParam === "image" || (typeParam === "video" && typeParam !== creativeType)) {
      handleCreativeTypeChange(typeParam);
      // Remove the query parameter after using it
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("type");
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  // Cancel/ignore in-flight history requests (prevents cross-type flashes on rapid tab switches)
  const createdHistoryAbortRef = useRef<AbortController | null>(null);
  const createdHistoryReqIdRef = useRef(0);
  const creativeTypeRef = useRef<"image" | "video">(creativeType);
  useEffect(() => {
    creativeTypeRef.current = creativeType;
  }, [creativeType]);
  useEffect(() => {
    return () => {
      createdHistoryAbortRef.current?.abort();
    };
  }, []);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    () =>
      loadFromLocalStorage(
        CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO,
        "Auto",
      ) as AspectRatio,
  );
  const [variations, setVariations] = useState<Variations>(
    () => loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, 4) as Variations,
  );
  const [avoidDangerZone, setAvoidDangerZone] = useState<boolean>(
    loadFromLocalStorage(
      CREATIVE_LIBRARY_STORAGE_KEYS.AVOID_DANGER_ZONE,
      false,
    ) as boolean,
  );
  const [videoType, setVideoType] = useState<VideoType>(() => {
    const stored = loadFromLocalStorage(
      CREATIVE_LIBRARY_STORAGE_KEYS.VIDEO_TYPE as string,
      "ugc_mirage",
    ) as VideoType;
    return stored;
  });

  // UGC video editing prompt selection (account-scoped)
  const ugcVideoEditingPromptStorageKey = useMemo(
    () =>
      (accountId !== undefined
        ? `${CREATIVE_LIBRARY_STORAGE_KEYS.UGC_VIDEO_EDITING_PROMPT_ID}:${accountId}`
        : CREATIVE_LIBRARY_STORAGE_KEYS.UGC_VIDEO_EDITING_PROMPT_ID) as string,
    [accountId],
  );
  const didInitUgcEditingPromptFromStorageRef = useRef(false);
  const [ugcVideoEditingPromptId, setUgcVideoEditingPromptId] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (didInitUgcEditingPromptFromStorageRef.current) return;
    didInitUgcEditingPromptFromStorageRef.current = true;
    const stored = loadFromLocalStorage<string | null>(
      ugcVideoEditingPromptStorageKey,
      null,
    );
    if (stored) setUgcVideoEditingPromptId(stored);
  }, [ugcVideoEditingPromptStorageKey]);
  useEffect(() => {
    saveToLocalStorage(ugcVideoEditingPromptStorageKey, ugcVideoEditingPromptId);
  }, [ugcVideoEditingPromptStorageKey, ugcVideoEditingPromptId]);
  const [model, setModel] = useState<Model>(() =>
    (coerceModel as (value: unknown) => Model)(
      loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, "gpt-image-1"),
    ),
  );

  // Moodboards (Style) - used for Image generation
  const {
    moodboards,
    moodboardsLoading,
    selectedMoodboardId,
    setSelectedMoodboardId,
    moodboardDialogOpen,
    setMoodboardDialogOpen,
    moodboardDialogInitialActiveId,
    openMoodboardDialog,
    onMoodboardsChange,
  } = useMoodboardsManager({ accountId });

  // Persist "Style" selection per account (moodboards are account-scoped)
  const moodboardStorageKey = useMemo(
    () =>
      accountId
        ? `${CREATIVE_LIBRARY_STORAGE_KEYS.MOODBOARD_ID}:${accountId}`
        : CREATIVE_LIBRARY_STORAGE_KEYS.MOODBOARD_ID,
    [accountId],
  );
  const didInitMoodboardFromStorageRef = useRef(false);
  useEffect(() => {
    if (didInitMoodboardFromStorageRef.current) return;
    didInitMoodboardFromStorageRef.current = true;
    const stored = loadFromLocalStorage<string | null>(moodboardStorageKey, null);
    if (stored) setSelectedMoodboardId(stored);
  }, [moodboardStorageKey, setSelectedMoodboardId]);

  useEffect(() => {
    // Don't persist optimistic ids; those are local-only.
    if (!selectedMoodboardId) {
      saveToLocalStorage(moodboardStorageKey, null);
      return;
    }
    if (selectedMoodboardId.startsWith("optimistic-")) return;
    saveToLocalStorage(moodboardStorageKey, selectedMoodboardId);
  }, [moodboardStorageKey, selectedMoodboardId]);

  useEffect(() => {
    // If stored id doesn't exist for this account, fallback to None.
    if (moodboardsLoading) return;
    if (!selectedMoodboardId) return;
    if (selectedMoodboardId.startsWith("optimistic-")) return;
    const exists = moodboards.some((m) => m.id === selectedMoodboardId);
    if (exists) return;
    setSelectedMoodboardId(null);
    saveToLocalStorage(moodboardStorageKey, null);
  }, [
    moodboardStorageKey,
    moodboardsLoading,
    moodboards,
    selectedMoodboardId,
    setSelectedMoodboardId,
  ]);

  const handleQuickCreateMoodboard = useCallback(() => {
    if (!accountId) return;
    const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const now = new Date().toISOString();
    const optimistic: Moodboard = {
      id: tempId,
      name: "My Moodboard",
      created_at: now,
      updated_at: now,
      files: [],
    };
    // Add immediately + select it as Style + open modal focused on it
    onMoodboardsChange((prev) => [...prev, optimistic]);
    setSelectedMoodboardId(tempId);
    openMoodboardDialog(tempId);
  }, [accountId, onMoodboardsChange, openMoodboardDialog, setSelectedMoodboardId]);
  const [creatingWorkflow, setCreatingWorkflow] = useState<boolean>(false);
  const [retryingWorkflowIds, setRetryingWorkflowIds] = useState<Record<string, boolean>>(
    {},
  );

  // Wrapper functions to save to localStorage when user manually changes values
  const handleCreativeTypeChange = (newCreativeType: "image" | "video") => {
    // Cancel/ignore any in-flight history request immediately to prevent cross-type flashes.
    createdHistoryReqIdRef.current += 1;
    createdHistoryAbortRef.current?.abort();

    // Prevent empty-state flash: show loader immediately for the next fetch.
    setLoadingHistory(true);
    setLoadingMore(false);

    setCreativeType(newCreativeType);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.CREATIVE_TYPE, newCreativeType);

    // For now, video supports only 9:16. Ensure we don't reuse an image aspect ratio.
    if (newCreativeType === "video") {
      setAspectRatio("9:16");
      saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO, "9:16");
    }

    // Reset + refetch history (Create mode only)
    setPage(1);
    setHasNext(false);
    setCreatedAdsMessages([]);
    setTimeout(() => {
      if (contentAreaRef.current) {
        contentAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 0);
  };

  const handleAspectRatioChange = (newAspectRatio: AspectRatio) => {
    setAspectRatio(newAspectRatio);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO, newAspectRatio);
  };

  const handleVariationsChange = (newVariations: Variations) => {
    setVariations(newVariations);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, newVariations);
  };

  const handleAvoidDangerZoneChange = (newAvoidDangerZone: boolean) => {
    setAvoidDangerZone(newAvoidDangerZone);
    saveToLocalStorage(
      CREATIVE_LIBRARY_STORAGE_KEYS.AVOID_DANGER_ZONE,
      newAvoidDangerZone,
    );
  };

  const handleModelChange = (newModel: Model) => {
    setModel(newModel);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, newModel);
  };

  const handleVideoTypeChange = (newVideoType: VideoType) => {
    setVideoType(newVideoType);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VIDEO_TYPE as string, newVideoType);
  };

  // library attachments
  const [libraryMedia, setLibraryMedia] = useState<LibraryMedia[]>([]);

  // popover state
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Helper to compute remaining attachment capacity (includes files, library media, and brand logos)
  const remainingCapacity = useMemo(
    () => ATTACHMENT_LIMIT - (files.length + libraryMedia.length),
    [files.length, libraryMedia.length],
  );

  const handleLibraryAttachmentChange = (attachments: LibraryMedia[]) => {
    // Respect overall attachment limit (includes files, library media, and brand logos)
    const capacity = remainingCapacity;
    if (capacity <= 0) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix={
            capacity === 0 ? "Attachment limit reached" : "Cannot attach more files"
          }
          subheadingPrefix={`You can attach up to ${ATTACHMENT_LIMIT} files in total.`}
          isError={false}
          isWarning={true}
        />,
      );
      return;
    }

    const allowedAttachments = attachments.slice(0, capacity);
    const filesById = new Map<string, LibraryMedia>();
    allowedAttachments.forEach((file) => {
      if (!filesById.has(file.file_id)) {
        filesById.set(file.file_id, file);
      }
    });
    setLibraryMedia(Array.from(filesById.values()));
    if (attachments.length > allowedAttachments.length) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix="Some files were not attached"
          subheadingPrefix={`Only ${ATTACHMENT_LIMIT} attachments allowed.`}
          isError={false}
          isWarning={true}
        />,
      );
    }

    // Close popover after library media attachment
    setPopoverOpen(false);
  };

  const handleRemoveLibraryAttachment = (fileToRemove: CustomFile) => {
    setLibraryMedia((prev) =>
      prev.filter(
        (media) =>
          (media.file_id && media.file_id !== fileToRemove.id) ||
          (!media.file_id && media.file_url !== fileToRemove.public_url),
      ),
    );
  };

  const handleFileUpload = async (selectedFiles: globalThis.File[]) => {
    // Enforce overall attachment limit (includes files, library media, and brand logos)
    const capacity = remainingCapacity;
    if (capacity <= 0) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix="Attachment limit reached"
          subheadingPrefix={`You can attach up to ${ATTACHMENT_LIMIT} files in total.`}
          isError={false}
          isWarning={true}
        />,
      );
      return;
    }

    // Trim selected files if they exceed remaining capacity
    const filesToProcess = selectedFiles.slice(0, capacity);

    if (selectedFiles.length > capacity) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix="Some files were not attached"
          subheadingPrefix={`Only ${ATTACHMENT_LIMIT} attachments allowed.`}
          isError={false}
          isWarning={true}
        />,
      );
    }

    if (filesToProcess.length > 0) {
      // Check if any file exceeds 100MB
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
      const oversizedFiles = filesToProcess.filter((file) => file.size > MAX_FILE_SIZE);

      if (oversizedFiles.length > 0) {
        toast.message(
          <ToastContent
            headingPrefix="File size too large"
            subheadingPrefix="Files must be under 100MB. Please select smaller files."
            isError={true}
          />,
        );
        return;
      }

      // Validate file types
      const invalidFiles = filesToProcess.filter(
        (file) =>
          !ALLOWED_FILE_TYPES.includes(file.type as (typeof ALLOWED_FILE_TYPES)[number]),
      );

      if (invalidFiles.length > 0) {
        toast.message(
          <ToastContent
            headingPrefix="Invalid file type"
            subheadingPrefix={`Only ${ALLOWED_FILE_TYPES.map((type) => type.split("/")[1].toUpperCase()).join(", ")} files are allowed.`}
            isError={true}
          />,
        );
        return;
      }

      // Create immediate preview items with temp IDs
      const newUploadingItems = filesToProcess.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const preview = URL.createObjectURL(file);
        return { file, preview, id: tempId };
      });
      setUploadingFiles(newUploadingItems);

      setIsFileUploading(true);
      try {
        const response = await uploadFilesAsync(
          filesToProcess,
          accountId || "",
          "generation",
        );
        setFiles((prev) => {
          // Use a Map to deduplicate by id, always keeping the first occurrence
          const filesById = new Map<string, CustomFile>();
          [...prev, ...response.files].forEach((file) => {
            if (!filesById.has(file.id)) {
              filesById.set(file.id, file);
            }
          });
          return Array.from(filesById.values());
        });
        setUploadingFiles([]);
        // Clean up object URLs to prevent memory leaks after upload
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } catch (error) {
        console.error("Error uploading files:", error);
        // Clean up uploading files and object URLs on error
        setUploadingFiles([]);
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } finally {
        setIsFileUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);

      void handleFileUpload(selectedFiles);
      // Close popover after file attachment
      setPopoverOpen(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Handle local files
    const droppedFiles = Array.from(e.dataTransfer.files);

    if (droppedFiles.length > 0) {
      void handleFileUpload(droppedFiles);
      return;
    }

    // Handle images from web pages (like Google Images)
    const items = Array.from(e.dataTransfer.items);

    for (const item of items) {
      // Handle file data directly

      const file = item.getAsFile();
      if (file) {
        void handleFileUpload([file]);
        return;
      }

      // Handle URLs (like when dragging from Google Images)
      if (item.type === "text/uri-list" || item.type === "text/plain") {
        const url = e.dataTransfer.getData(item.type);

        if (url && (url.startsWith("http") || url.startsWith("data:"))) {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const file = new globalThis.File([blob], `file.${blob.type.split("/")[1]}`, {
              type: blob.type,
            });
            void handleFileUpload([file]);
            return;
          } catch (error) {
            console.error("Error fetching file from URL:", error);
          }
        }
      }

      // Handle HTML content (sometimes contains image data)
      if (item.type === "text/html") {
        const html = e.dataTransfer.getData("text/html");
        const srcMatch = html.match(/<(?:img|video|source)[^>]+src="([^"]+)"/);

        if (srcMatch && srcMatch[1]) {
          const fileUrl = srcMatch[1];
          try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const file = new globalThis.File([blob], `file.${blob.type.split("/")[1]}`, {
              type: blob.type,
            });
            void handleFileUpload([file]);
            return;
          } catch (error) {
            console.error("Error fetching file from HTML URL:", error);
          }
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const mediaFiles = items
      .map((item) => item.getAsFile())
      .filter((file): file is globalThis.File => file !== null);

    if (mediaFiles.length > 0) {
      e.preventDefault();
      void handleFileUpload(mediaFiles);
    }
  };

  const handleRemoveFile = (fileToRemove: CustomFile) => {
    setFiles((prev) =>
      prev.filter(
        (file) =>
          (file.id && file.id !== fileToRemove.id) ||
          (!file.id && file.public_url !== fileToRemove.public_url),
      ),
    );
  };

  const getCreatedAdHistory = useMemo(
    () =>
      async (
        pageNum: number = 1,
        order: SortOrder = sortOrder,
        initialLoad: boolean = false,
        type: "image" | "video" = creativeType,
      ) => {
        if (!accountId) return;
        const sort_type = order === "newest-first" ? "newest" : "oldest";
        // Abort previous request (especially important for rapid Images/Videos switching).
        createdHistoryAbortRef.current?.abort();
        const controller = new AbortController();
        createdHistoryAbortRef.current = controller;
        const reqId = (createdHistoryReqIdRef.current += 1);
        try {
          if (initialLoad || pageNum === 1) {
            setLoadingHistory(true);
          } else {
            setLoadingMore(true);
          }

          const res = await fetchGenerateAdHistory(
            accountId,
            pageNum,
            pageSize,
            sort_type,
            type,
            { signal: controller.signal },
          );

          // Ignore stale responses (e.g. rapid tab switching).
          if (reqId !== createdHistoryReqIdRef.current) return;
          if (creativeTypeRef.current !== type) return;

          setCreatedAdsMessages((prev) =>
            pageNum === 1 ? res.data : [...prev, ...res.data],
          );
          setHasNext(res.pagination.has_next);
          setPage(pageNum);
        } catch (error) {
          // Ignore aborts.
          if (controller.signal.aborted) return;
          console.error(error);
        } finally {
          // Only the latest request may toggle loading flags.
          const isLatest = reqId === createdHistoryReqIdRef.current;
          if (isLatest) {
            if (initialLoad || pageNum === 1) {
              setLoadingHistory(false);
            }
            setLoadingMore(false);
          }
        }
      },
    [accountId, sortOrder, creativeType],
  );

  const getWorkflowHistory = useMemo(
    () =>
      async (
        pageNum: number = 1,
        order: SortOrder = sortOrder,
        initialLoad: boolean = false,
      ) => {
        if (!accountId) return;
        const sort_type = order === "newest-first" ? "newest" : "oldest";
        try {
          if (initialLoad || pageNum === 1) {
            setLoadingWorkflows(true);
          } else {
            setWorkflowLoadingMore(true);
          }

          const res = await fetchWorkflowsListTyped(
            accountId,
            pageNum,
            pageSize,
            sort_type,
          );
          setWorkflowHistory((prev) =>
            pageNum === 1 ? res.data : [...prev, ...res.data],
          );
          setWorkflowHasNext(res.pagination.has_next);
          setWorkflowPage(pageNum);
        } catch (error) {
          console.error(error);
        } finally {
          if (initialLoad || pageNum === 1) setLoadingWorkflows(false);
          setWorkflowLoadingMore(false);
        }
      },
    [accountId, sortOrder, fetchWorkflowsListTyped],
  );

  // Fetch first page on mount & whenever accountId / sortOrder / mode changes
  useEffect(() => {
    if (createMode === "generate_ads") {
      void getCreatedAdHistory(1, sortOrder, true, creativeType);
    } else {
      void getWorkflowHistory(1, sortOrder, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, sortOrder, createMode, creativeType]);

  // Pagination handler (throttled) — kept separate from the high-freq header updates
  const paginationHandler = useMemo(
    () =>
      throttle((container: HTMLDivElement) => {
        const threshold = 600; // px from bottom to trigger load
        if (
          container.scrollHeight - container.scrollTop - container.clientHeight >=
          threshold
        ) {
          return;
        }
        if (createMode === "generate_ads") {
          if (hasNext && !loadingMore && !loadingHistory) {
            void getCreatedAdHistory(page + 1, sortOrder, false, creativeType);
          }
          return;
        }
        if (workflowHasNext && !workflowLoadingMore && !loadingWorkflows) {
          void getWorkflowHistory(workflowPage + 1, sortOrder);
        }
      }, 200),
    [
      createMode,
      hasNext,
      loadingMore,
      loadingHistory,
      page,
      workflowHasNext,
      workflowLoadingMore,
      loadingWorkflows,
      workflowPage,
      sortOrder,
      getCreatedAdHistory,
      getWorkflowHistory,
      creativeType,
    ],
  );

  // Attach scroll listener for smooth header + pagination
  useEffect(() => {
    const container = contentAreaRef.current;
    if (!container) return;

    const onScroll = () => {
      // Only hide header if there's enough scrollable content (at least TITLE_HEIGHT + buffer)
      // This prevents flickering when content barely overflows
      const scrollableAmount = container.scrollHeight - container.clientHeight;
      const hasEnoughScrollableContent = scrollableAmount > TITLE_HEIGHT + 20;

      // Map scrollTop 0→headerHeight to header slide, but only if there's enough content
      const newOffset = hasEnoughScrollableContent
        ? -Math.min(container.scrollTop, TITLE_HEIGHT) // -56 (hidden) → 0 (visible)
        : 0; // Keep header visible when content barely scrollable
      headerOffset.set(newOffset);

      // Handle pagination at throttled cadence
      paginationHandler(container);
    };

    // Initial position (in case user reloads mid-scroll)
    onScroll();

    container.addEventListener("scroll", onScroll);
    return () => {
      container.removeEventListener("scroll", onScroll);
      paginationHandler.cancel();
    };
  }, [headerOffset, paginationHandler]);

  const handleGenerateAd = async () => {
    if (!accountId) return;

    // Store current input values before clearing
    const input = prompt;
    const currentFiles = [...files];
    const currentLibraryMedia = [...libraryMedia];
    const file_ids = files.map((f) => f.id);
    const library_file_ids = libraryMedia.map((m) => m.file_id);

    // Clear inputs immediately
    setPrompt("");
    setFiles([]);
    setLibraryMedia([]);

    // Generate optimistic ID
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const now = new Date().toISOString();
    const currentAspectRatio = aspectRatio === "Auto" ? null : aspectRatio;

    // Create optimistic ad_creatives based on variations count
    const optimisticAdCreatives: AdCreative[] = Array.from(
      { length: variations },
      (_, i) => {
        const creativeId = `${optimisticId}-creative-${i}`;
        const imageId = `${optimisticId}-image-${i}`;
        const videoId = `${optimisticId}-video-${i}`;

        return {
          id: creativeId,
          name: "",
          folder_id: null,
          creative: {
            bodies: [],
            images:
              creativeType === "image"
                ? [
                    {
                      id: imageId,
                      url: "",
                      hash: "",
                      width: 0,
                      height: 0,
                      permalink_url: "",
                      status: "pending" as const,
                      aspect_ratio: currentAspectRatio || undefined,
                      progress: undefined,
                      edit_configuration: {},
                    },
                  ]
                : [],
            titles: [],
            videos:
              creativeType === "video"
                ? [
                    {
                      video_id: videoId,
                      thumbnail_url: "",
                      permalink_url: "",
                      source: "",
                      aspect_ratio: currentAspectRatio || undefined,
                    },
                  ]
                : [],
            thumbnail_url: "",
          },
          creative_type: creativeType,
          extra_data: {},
          status: "pending" as const,
          progress: "Starting to generate ad creative",
          created_at: now,
          updated_at: now,
        };
      },
    );

    // Convert library media to file format for optimistic display
    const optimisticLibraryFiles: CustomFile[] = currentLibraryMedia.map((media) => ({
      id: media.file_id,
      file_name: media.file_name,
      public_url: media.file_url,
    }));

    // Create optimistic message (_listKey keeps React key stable when replaced by API data to avoid flicker)
    const optimisticMessage: GenerateAdData = {
      id: optimisticId,
      _listKey: optimisticId,
      prompt: input,
      files: currentFiles,
      library_files: optimisticLibraryFiles,
      created_at: now,
      updated_at: now,
      ad_creatives: optimisticAdCreatives,
    };

    // Add optimistic message to UI immediately
    setCreatedAdsMessages((prev) => {
      if (sortOrder === "newest-first") {
        return [optimisticMessage, ...prev];
      } else {
        return [...prev, optimisticMessage];
      }
    });

    // Scroll to appropriate position immediately
    setTimeout(() => {
      if (contentAreaRef.current) {
        if (sortOrder === "newest-first") {
          contentAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          contentAreaRef.current.scrollTo({
            top: contentAreaRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }
    }, 100);

    try {
      const res = await generateAd(accountId, {
        prompt: input,
        file_ids: file_ids,
        creative_type: creativeType,
        ...(creativeType === "video" && { video_type: videoType }),
        ...(creativeType === "video" &&
          (videoType === "ugc_mirage" || videoType === "ugc_veo") && {
            ugc_video_editing_prompt_id: ugcVideoEditingPromptId,
          }),
        library_file_ids: library_file_ids,
        aspect_ratio: currentAspectRatio,
        number_of_media: variations,
        avoid_danger_zone: avoidDangerZone,
        ...(creativeType === "image" && selectedMoodboardId
          ? { moodboard_id: selectedMoodboardId }
          : {}),
        ...(creativeType === "image" && { model }),
      });

      // Replace optimistic message with real data from API
      setCreatedAdsMessages((prev) =>
        prev.map((msg) => (msg.id === optimisticId ? res.data : msg)),
      );
    } catch (error) {
      console.error("Error generating ad:", error);
      // Remove optimistic message on error
      setCreatedAdsMessages((prev) => prev.filter((msg) => msg.id !== optimisticId));
      // Show error toast
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix="Failed to generate ad"
          subheadingPrefix="Please try again."
          isError={true}
        />,
      );
    }
  };

  const handleCreateWorkflow = async () => {
    if (!accountId) return;
    const input = prompt;
    const file_ids = files.map((f) => f.id);
    const library_file_ids = libraryMedia.map((m) => m.file_id);
    const derivedName = input.trim().slice(0, 60) || "New workflow";
    setPrompt("");
    setFiles([]);
    setLibraryMedia([]);
    try {
      setCreatingWorkflow(true);
      const res = await createWorkflowTyped(accountId, {
        workflow_name: derivedName,
        prompt: input,
        file_ids,
        library_file_ids,
      });
      const newWorkflow: CreativeWorkflow = {
        ...res.data.workflow,
        creative_prompts: res.data.workflow.creative_prompts || [],
      };
      setWorkflowHistory((prev) => {
        if (sortOrder === "newest-first") return [newWorkflow, ...prev];
        return [...prev, newWorkflow];
      });
      setTimeout(() => {
        if (contentAreaRef.current) {
          if (sortOrder === "newest-first") {
            contentAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            contentAreaRef.current.scrollTo({
              top: contentAreaRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        }
      }, 100);
    } catch (error) {
      console.error("Error creating workflow:", error);
    } finally {
      setCreatingWorkflow(false);
    }
  };

  const handleRetryWorkflow = async (workflowId: string) => {
    if (!accountId) return;
    setRetryingWorkflowIds((prev) => ({ ...prev, [workflowId]: true }));
    try {
      const res = await retryWorkflowTyped(accountId, workflowId);
      const updated = res.data?.workflow;
      if (updated) {
        setWorkflowHistory((prev) =>
          prev.map((wf) =>
            wf.id === workflowId
              ? {
                  ...wf,
                  ...updated,
                  creative_prompts: updated.creative_prompts || wf.creative_prompts || [],
                }
              : wf,
          ),
        );
        return;
      }
      // Fallback: if retry doesn't return workflow, refetch by id
      const fetched = await fetchWorkflowByIdTyped(accountId, workflowId);
      setWorkflowHistory((prev) =>
        prev.map((wf) =>
          wf.id === workflowId
            ? {
                ...wf,
                ...fetched.data.workflow,
                creative_prompts:
                  fetched.data.workflow.creative_prompts || wf.creative_prompts || [],
              }
            : wf,
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setRetryingWorkflowIds((prev) => ({ ...prev, [workflowId]: false }));
    }
  };

  const handleBookmark = useCallback(
    (folderId: string | null, msgId: string, creativeId: string) => {
      if (!accountId) return;
      updateCreative(accountId, creativeId, folderId)
        .then((res) => {
          // Update the ad creative in the local state
          setCreatedAdsMessages((prevMessages) =>
            prevMessages.map((message) => {
              if (message.id === msgId) {
                return {
                  ...message,
                  ad_creatives: message.ad_creatives.map((creative) =>
                    creative.id === creativeId ? res.data : creative,
                  ),
                };
              }
              return message;
            }),
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [accountId],
  );

  // Edit Image
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editingImage, setEditingImage] = useState<Image | null>(null);

  // Edit Image V2 (for Edit button)
  const [openEditDialogV2, setOpenEditDialogV2] = useState<boolean>(false);
  const [editingAdV2, setEditingAdV2] = useState<AdCreative | null>(null);
  const [currentImageIndexByCreativeId, setCurrentImageIndexByCreativeId] = useState<
    Record<string, number>
  >({});

  const handleEditDialog = useCallback(
    (open: boolean, image?: Image, adCreative?: AdCreative) => {
      setOpenEditDialog(open);
      setEditingImage(image || null);
      setEditingAdV2(adCreative || null);
    },
    [],
  );

  const handleEditDialogV2 = useCallback((open: boolean, ad?: AdCreative) => {
    setOpenEditDialogV2(open);
    setEditingAdV2(ad || null);
  }, []);

  // Update dashboard when creative is updated from polling / edit dialogs
  const handleDialogCreativeUpdate = useCallback(
    (updated: AdCreative) => {
      if (createMode === "generate_ads") {
        setCreatedAdsMessages((prevMessages) =>
          prevMessages.map((message) => ({
            ...message,
            ad_creatives: message.ad_creatives.map((ad) =>
              ad.id === updated.id ? updated : ad,
            ),
          })),
        );
      } else {
        setWorkflowHistory((prev) =>
          prev.map((wf) => ({
            ...wf,
            creative_prompts: (wf.creative_prompts || []).map((cp) => ({
              ...cp,
              ad_creatives: cp.ad_creatives.map((ad) =>
                ad.id === updated.id ? updated : ad,
              ),
            })),
          })),
        );
      }
      // Also keep the editing reference fresh if it's the same item
      setEditingAdV2((prev) => (prev && prev.id === updated.id ? updated : prev));
    },
    [createMode],
  );

  const handleBookmarkWorkflow = useCallback(
    (
      folderId: string | null,
      workflowId: string,
      creativePromptId: string,
      creativeId: string,
    ) => {
      if (!accountId) return;
      updateCreative(accountId, creativeId, folderId)
        .then((res) => {
          setWorkflowHistory((prev) =>
            prev.map((wf) => {
              if (wf.id !== workflowId) return wf;
              return {
                ...wf,
                creative_prompts: (wf.creative_prompts || []).map((cp) => {
                  if (cp.id !== creativePromptId) return cp;
                  return {
                    ...cp,
                    ad_creatives: cp.ad_creatives.map((ad) =>
                      ad.id === creativeId ? res.data : ad,
                    ),
                  };
                }),
              };
            }),
          );
        })
        .catch((error) => console.error(error));
    },
    [accountId],
  );

  const pendingWorkflowIds = useMemo(
    () =>
      (workflowHistory || []).filter((wf) => wf.status === "pending").map((wf) => wf.id),
    [workflowHistory],
  );
  const pendingWorkflowIdsKey = useMemo(
    () => pendingWorkflowIds.join("|"),
    [pendingWorkflowIds],
  );

  // Poll workflows that are still pending and keep updating UI
  useEffect(() => {
    if (createMode !== "workflow") return;
    if (!accountId) return;
    if (!pendingWorkflowIdsKey) return;
    const idsToPoll = pendingWorkflowIdsKey.split("|");

    const controller = new AbortController();
    let cancelled = false;

    const tick = async (): Promise<void> => {
      try {
        const results = await Promise.all(
          idsToPoll.map((id) =>
            fetchWorkflowByIdTyped(accountId, id, { signal: controller.signal }),
          ),
        );
        if (cancelled) return;
        const byId = new Map<string, CreativeWorkflow>();
        results.forEach((r) => {
          const wf = r.data.workflow;
          if (wf?.id) byId.set(wf.id, wf);
        });
        setWorkflowHistory((prev) =>
          prev.map((wf) => {
            const updated = byId.get(wf.id);
            if (!updated) return wf;
            return {
              ...wf,
              ...updated,
              creative_prompts: updated.creative_prompts || wf.creative_prompts || [],
            };
          }),
        );
      } catch (error) {
        // ignore abort errors
        if (controller.signal.aborted) return;
        console.error(error);
      }
    };

    void tick();
    const interval = window.setInterval(() => {
      void tick();
    }, 7000);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
    };
  }, [createMode, accountId, pendingWorkflowIdsKey, fetchWorkflowByIdTyped]);

  // Handle dialog close and update current image index
  const handleEditDialogV2Close = (
    open: boolean,
    activeImageId?: string,
    updatedCreative?: AdCreative,
  ) => {
    if (!open && editingAdV2 && activeImageId) {
      const updatedAdV2 = updatedCreative || editingAdV2;
      const availableImages = updatedAdV2?.creative?.images || [];
      const newIndex = availableImages.findIndex((img) => img.id === activeImageId);
      if (newIndex !== -1) {
        setCurrentImageIndexByCreativeId((prev) => ({
          ...prev,
          [updatedAdV2.id]: newIndex,
        }));
      }
    }
    setOpenEditDialogV2(open);
    if (!open) {
      setEditingAdV2(null);
    }
  };

  const handleEditImageAd = async (
    edit_prompt: string,
    mask_file: globalThis.File | undefined,
    original_file: globalThis.File,
    creative_type: "image" | "video",
    aspect_ratio: string | null,
    number_of_media: number,
    avoid_danger_zone: boolean,
    file_ids: string[],
    library_file_ids: string[],
    editModel?: Model,
  ) => {
    if (!accountId) return;

    // Generate optimistic ID
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const now = new Date().toISOString();

    // Create optimistic ad_creatives based on variations count
    const optimisticAdCreatives: AdCreative[] = Array.from(
      { length: number_of_media },
      (_, i) => {
        const creativeId = `${optimisticId}-creative-${i}`;
        const imageId = `${optimisticId}-image-${i}`;

        return {
          id: creativeId,
          name: "",
          folder_id: null,
          creative: {
            bodies: [],
            images:
              creative_type === "image"
                ? [
                    {
                      id: imageId,
                      url: "",
                      hash: "",
                      width: 0,
                      height: 0,
                      permalink_url: "",
                      status: "pending" as const,
                      aspect_ratio: aspect_ratio || undefined,
                      progress: undefined,
                      edit_configuration: {},
                    },
                  ]
                : [],
            titles: [],
            videos: [],
            thumbnail_url: "",
          },
          creative_type: creative_type,
          extra_data: {},
          status: "pending" as const,
          progress: "Starting to generate ad creative",
          created_at: now,
          updated_at: now,
        };
      },
    );

    // Create optimistic message (_listKey keeps React key stable when replaced by API data to avoid flicker)
    const optimisticMessage: GenerateAdData = {
      id: optimisticId,
      _listKey: optimisticId,
      prompt: edit_prompt,
      files: [],
      library_files: [],
      created_at: now,
      updated_at: now,
      ad_creatives: optimisticAdCreatives,
    };

    // Add optimistic message to UI immediately
    setCreatedAdsMessages((prev) => {
      if (sortOrder === "newest-first") {
        return [optimisticMessage, ...prev];
      } else {
        return [...prev, optimisticMessage];
      }
    });

    // Scroll to appropriate position immediately
    setTimeout(() => {
      if (contentAreaRef.current) {
        if (sortOrder === "newest-first") {
          contentAreaRef.current.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          contentAreaRef.current.scrollTo({
            top: contentAreaRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }
    }, 100);

    // Upload files in background
    try {
      // Upload original file
      const original_res = await uploadFilesAsync([original_file], accountId);
      const original_file_id = original_res.files[0]?.id;

      if (!original_file_id) {
        throw new Error("Failed to upload original file");
      }

      // Upload mask file if present
      let mask_file_id: string | undefined;
      if (mask_file) {
        const mask_res = await uploadFilesAsync([mask_file], accountId);
        mask_file_id = mask_res.files[0]?.id;
        if (!mask_file_id) {
          throw new Error("Failed to upload mask file");
        }
      }

      // Prepare file IDs (original + additional files, but NOT mask - mask is separate)
      const all_file_ids = [original_file_id, ...file_ids];

      // Call generateAd API
      const res = await generateAd(accountId, {
        prompt: edit_prompt,
        file_ids: all_file_ids,
        creative_type: creative_type,
        ...(mask_file_id && { mask_file_id }),
        ...(library_file_ids.length > 0 && { library_file_ids }),
        aspect_ratio: aspect_ratio,
        number_of_media: number_of_media,
        avoid_danger_zone: avoid_danger_zone,
        ...(creative_type === "image" && selectedMoodboardId
          ? { moodboard_id: selectedMoodboardId }
          : {}),
        ...(creative_type === "image" && editModel && { model: editModel }),
      });

      // Replace optimistic message with real data from API
      setCreatedAdsMessages((prev) =>
        prev.map((msg) => (msg.id === optimisticId ? res.data : msg)),
      );
    } catch (error) {
      console.error("Error generating ad:", error);
      // Remove optimistic message on error
      setCreatedAdsMessages((prev) => prev.filter((msg) => msg.id !== optimisticId));
      // Show error toast
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix="Failed to generate ad"
          subheadingPrefix="Please try again."
          isError={true}
        />,
      );
    }
  };

  const handleCopyPrompt = useCallback((data: PromptCopyData) => {
    setPrompt(data.prompt);
    setFiles(data.files);
    setLibraryMedia(
      data.libraryFiles.map((f) => ({
        id: f.id,
        file_id: f.id,
        file_name: f.file_name,
        file_type: "",
        file_url: f.public_url,
        created_at: "",
        updated_at: "",
      })),
    );
  }, []);

  // Stable callback for loading more messages - prevents VirtualizedMessagesList re-renders
  const handleLoadMore = useCallback(() => {
    if (hasNext && !loadingMore && !loadingHistory) {
      void getCreatedAdHistory(page + 1, sortOrder, false, creativeType);
    }
  }, [
    hasNext,
    loadingMore,
    loadingHistory,
    getCreatedAdHistory,
    page,
    sortOrder,
    creativeType,
  ]);

  // Stable callback for updating image index - prevents unnecessary re-renders in child components
  const handleSetCurrentImageIndex = useCallback(
    (updater: React.SetStateAction<Record<string, number>>) => {
      setCurrentImageIndexByCreativeId(updater);
    },
    [],
  );

  const TabIcon = tabItem.icon;

  return (
    <>
      <div className="relative h-full w-full flex">
        <div className="relative flex flex-col w-full h-full flex-1 min-w-0">
          <div className={`ai-glow-wrapper ai-glow--input pointer-events-none absolute bottom-0 left-0 right-0 z-[15] h-[80px] ${isInputFocused ? "ai-glow-active" : ""}`}>
            <div className="ai-glow-edge ai-glow-edge-bottom" />
          </div>
          {/* sorting */}
          <div className="flex gap-2 pt-[8px] pb-2 pl-[9px] pr-[14px]">
            {sidebarCollapsed && (
              <div className="h-[32px] w-[32px] shrink-0 bg-transparent"></div>
            )}
            {createMode === "workflow" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="sq-outline"
                    className="flex items-center gap-2 w-[154px]"
                  >
                    <ArrowUpDown
                      size={14}
                      className="text-brand-primary-400"
                      strokeWidth={1.8}
                    />
                    <span className="font-['Helvetica_Neue'] text-[11px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-800">
                      {displayText}
                    </span>
                    <ChevronDown
                      size={14}
                      className="text-brand-primary-400"
                      strokeWidth={1.8}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[154px] !rounded-[8px]">
                  <DropdownMenuItem
                    onClick={() => handleSort("newest-first")}
                    className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
                  >
                    Latest to Oldest
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleSort("oldest-first")}
                    className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
                  >
                    Oldest to Latest
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {createMode === "generate_ads" && (
              <Tabs
                value={creativeType}
                onValueChange={(v) => handleCreativeTypeChange(v as "image" | "video")}
              >
                <TabsList className="h-8 bg-white rounded-[8px] p-[2px] ring-1 ring-border-gray">
                  <TabsTrigger
                    value="image"
                    className="h-7 rounded-[8px] px-2 py-0 gap-[6px] data-[state=active]:rounded-[6px] data-[state=active]:bg-[#FFF4F0] data-[state=active]:text-brand-orange data-[state=active]:shadow-none data-[state=active]:outline data-[state=active]:outline-1 data-[state=active]:outline-[#FFB599] data-[state=active]:outline-offset-[-1px]"
                  >
                    <RiImage2Line
                      size={14}
                      className={
                        creativeType === "image"
                          ? "text-brand-orange"
                          : "text-brand-primary-400"
                      }
                    />
                    <span
                      className={`font-['Helvetica Neue'] text-[11px] font-medium not-italic leading-[19.16px] tracking-[0.02em] ${creativeType === "image" ? "" : "text-[rgba(2,24,89,0.80)]"}`}
                    >
                      Images
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="video"
                    className="h-7 rounded-[8px] px-2 py-0 gap-[6px] data-[state=active]:rounded-[6px] data-[state=active]:bg-[#FFF4F0] data-[state=active]:text-brand-orange data-[state=active]:shadow-none data-[state=active]:outline data-[state=active]:outline-1 data-[state=active]:outline-[#FFB599] data-[state=active]:outline-offset-[-1px] disabled:opacity-50"
                  >
                    <RiVideoLine
                      size={14}
                      className={
                        creativeType === "video"
                          ? "text-brand-orange"
                          : "text-brand-primary-400"
                      }
                    />
                    <span
                      className={`font-['Helvetica Neue'] text-[11px] font-medium not-italic leading-[19.16px] tracking-[0.02em] ${creativeType === "video" ? "" : "text-[rgba(2,24,89,0.80)]"}`}
                    >
                      Videos
                    </span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {showAskAI && (
              <TooltipProvider delayDuration={500}>
                <Tooltip>
                  <TooltipTrigger
                    className={`${isCopilotOpen || !selectedAccount?.id ? "hidden" : ""} ml-auto`}
                  >
                    <div
                      onClick={() =>
                        !isCopilotOpen && selectedAccount?.id && setIsCopilotOpen(true)
                      }
                    >
                      <div
                        className={`h-8 flex items-center gap-[6px] px-[14px] py-[6px] squircle-mask bg-gradient-to-r from-[#FF4500]/90 to-[#FF5188]/90 rounded-[8px] ${
                          isCopilotOpen || !selectedAccount?.id
                            ? "cursor-not-allowed opacity-0 hidden"
                            : "cursor-pointer hover:text-accent-foreground"
                        }`}
                      >
                        <RiChatSmileAiLine
                          size={16}
                          className="flex-shrink-0 text-white"
                        />
                        <span className="text-white text-[12px] font-medium tracking-[0.24px] relative top-[1px] whitespace-nowrap">
                          Ask AI
                        </span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    align="end"
                    className="text-white text-[12px] py-2 px-3 min-w-0 max-w-[240px] h-7 squircle squircle-fill-[black]"
                  >
                    <span className="relative -top-[2px]">
                      Hello, {userName?.split(" ")?.[0] ?? "Stranger"}
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {/* Header — single element animates height + slide + fade */}
          <motion.div
            style={{
              opacity: headerOpacity,
              height: headerHeight,
              overflow: "hidden",
            }}
            className="flex items-end gap-2 pl-[9px]"
          >
            <ReportTitle
              title={tabItem.label}
              icon={<TabIcon size={16} className="text-brand-orange" />}
              iconBackgroundColor="#FF45000F"
            />
          </motion.div>
          {/* Main */}
          <div className="flex-1 flex flex-col h-full relative">
            {/* Content Area */}
            <div
              ref={contentAreaRef}
              className="absolute inset-0 overflow-y-auto px-[9px] py-[8px] pb-[84px]"
            >
              {createMode === "generate_ads" ? (
                loadingHistory ? (
                  <div className="flex items-center justify-center h-[80%] w-full">
                    <PageLoader />
                  </div>
                ) : createdAdsMessages.length === 0 ? (
                  <div className="mt-16">{EmptyState}</div>
                ) : (
                  <VirtualizedMessagesList
                    messages={createdAdsMessages}
                    containerRef={contentAreaRef}
                    handleBookmark={handleBookmark}
                    onCreativeUpdate={handleDialogCreativeUpdate}
                    setCreatedAdsMessages={setCreatedAdsMessages}
                    sortOrder={sortOrder}
                    handleEditDialog={handleEditDialog}
                    handleEditDialogV2={handleEditDialogV2}
                    currentImageIndexByCreativeId={currentImageIndexByCreativeId}
                    setCurrentImageIndexByCreativeId={handleSetCurrentImageIndex}
                    loadingMore={loadingMore}
                    hasNext={hasNext}
                    onLoadMore={handleLoadMore}
                    onCopyPrompt={handleCopyPrompt}
                  />
                )
              ) : loadingWorkflows ? (
                <div className="flex items-center justify-center h-[80%] w-full">
                  <PageLoader />
                </div>
              ) : workflowHistory.length === 0 ? (
                <div className="mt-16">{EmptyState}</div>
              ) : (
                <div className="w-full flex flex-col gap-6 pb-[72px]">
                  {workflowHistory.map((wf, wfIndex) => (
                    <RenderWorkflow
                      key={wf.id}
                      workflow={wf}
                      workflowIndex={wfIndex}
                      totalWorkflows={workflowHistory.length}
                      onBookmark={handleBookmarkWorkflow}
                      onCreativeUpdate={handleDialogCreativeUpdate}
                      onRetry={(workflowId) => void handleRetryWorkflow(workflowId)}
                      retrying={!!retryingWorkflowIds[wf.id]}
                      handleEditDialog={handleEditDialog}
                      handleEditDialogV2={handleEditDialogV2}
                      currentImageIndexByCreativeId={currentImageIndexByCreativeId}
                      setCurrentImageIndexByCreativeId={handleSetCurrentImageIndex}
                      onCopyPrompt={handleCopyPrompt}
                    />
                  ))}
                  {workflowLoadingMore && (
                    <div className="flex justify-center py-4">
                      <ItemLoader />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Chat Input  */}
          <div
            className="z-20 w-full max-w-[760px] mb-4 absolute bottom-0 left-1/2 -translate-x-1/2 px-[9px]"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <CreativeInputSection
              accountId={accountId}
              prompt={prompt}
              creativeType={creativeType}
              videoType={videoType}
              aspectRatio={aspectRatio}
              variations={variations}
              avoidDangerZone={avoidDangerZone}
              model={model}
              ugcVideoEditingPromptId={ugcVideoEditingPromptId}
              moodboards={moodboards}
              moodboardId={selectedMoodboardId}
              moodboardsLoading={moodboardsLoading}
              files={files}
              libraryMedia={libraryMedia}
              uploadingFiles={uploadingFiles}
              isFileUploading={isFileUploading}
              submitting={createMode === "workflow" ? creatingWorkflow : false}
              popoverOpen={popoverOpen}
              onPromptChange={setPrompt}
              onCreativeTypeChange={handleCreativeTypeChange}
              onVideoTypeChange={handleVideoTypeChange}
              onAspectRatioChange={handleAspectRatioChange}
              onVariationsChange={handleVariationsChange}
              onAvoidDangerZoneChange={handleAvoidDangerZoneChange}
              onModelChange={handleModelChange}
              onMoodboardChange={setSelectedMoodboardId}
              onCreateMoodboard={() => void handleQuickCreateMoodboard()}
              onEditMoodboard={(id) => openMoodboardDialog(id)}
              onUgcVideoEditingPromptChange={setUgcVideoEditingPromptId}
              onFileChange={handleFileChange}
              onLibraryAttachmentChange={handleLibraryAttachmentChange}
              onRemoveFile={handleRemoveFile}
              onRemoveLibraryAttachment={handleRemoveLibraryAttachment}
              onPaste={handlePaste}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onSubmit={() => {
                if (createMode === "generate_ads") {
                  void handleGenerateAd();
                } else {
                  void handleCreateWorkflow();
                }
              }}
              onPopoverOpenChange={setPopoverOpen}
              showCreativeTypeDropdown={false}
              fileInputRef={fileInputRef}
              hideGenerationControls={createMode === "workflow"}
              onFocusChange={setIsInputFocused}
            />
          </div>
        </div>

        <Copilot
          open={isCopilotOpen}
          onChatOpenChange={setIsCopilotOpen}
          selectedAccount={selectedAccount}
          mode="create"
          showSectionDropdown={false}
        />
      </div>
      {accountId && (
        <MoodboardManagerDialog
          open={moodboardDialogOpen}
          onOpenChange={setMoodboardDialogOpen}
          accountId={accountId}
          moodboards={moodboards}
          selectedMoodboardId={selectedMoodboardId}
          initialActiveMoodboardId={moodboardDialogInitialActiveId}
          onSelectMoodboardId={setSelectedMoodboardId}
          onMoodboardsChange={onMoodboardsChange}
        />
      )}
      {openEditDialog && (
        <EditImageDialog
          open={openEditDialog}
          onOpenChange={handleEditDialog}
          image={editingImage}
          onEdit={(
            edit_prompt,
            mask_file,
            original_file,
            creative_type,
            aspect_ratio,
            number_of_media,
            avoid_danger_zone,
            file_ids,
            library_file_ids,
            editModel,
          ) => {
            void handleEditImageAd(
              edit_prompt,
              mask_file,
              original_file,
              creative_type,
              aspect_ratio,
              number_of_media,
              avoid_danger_zone,
              file_ids,
              library_file_ids,
              editModel,
            );
          }}
          onEditClick={() => {
            if (editingAdV2) {
              setOpenEditDialog(false);
              handleEditDialogV2(true, editingAdV2);
            }
          }}
          // Settings props (synced)
          aspectRatio={aspectRatio}
          variations={variations}
          avoidDangerZone={avoidDangerZone}
          model={model}
          onAspectRatioChange={handleAspectRatioChange}
          onVariationsChange={handleVariationsChange}
          onAvoidDangerZoneChange={handleAvoidDangerZoneChange}
          onModelChange={handleModelChange}
        />
      )}
      {openEditDialogV2 && (
        <EditImageDialogV2
          open={openEditDialogV2}
          onOpenChange={handleEditDialogV2Close}
          adCreative={editingAdV2}
          onCreativeUpdate={handleDialogCreativeUpdate}
          initialActiveImageId={
            editingAdV2
              ? (() => {
                  const availableImages = editingAdV2.creative?.images || [];
                  const currentIndex = currentImageIndexByCreativeId[editingAdV2.id] || 0;
                  return availableImages[currentIndex]?.id;
                })()
              : undefined
          }
        />
      )}
    </>
  );
};

const RenderWorkflow = memo(
  ({
    workflow,
    workflowIndex,
    totalWorkflows,
    onBookmark,
    onCreativeUpdate,
    onRetry,
    retrying,
    handleEditDialog,
    handleEditDialogV2,
    currentImageIndexByCreativeId,
    setCurrentImageIndexByCreativeId,
    onCopyPrompt,
  }: {
    workflow: CreativeWorkflow;
    workflowIndex: number;
    totalWorkflows: number;
    onBookmark: (
      folderId: string | null,
      workflowId: string,
      creativePromptId: string,
      creativeId: string,
    ) => void;
    onCreativeUpdate: (updated: AdCreative) => void;
    onRetry: (workflowId: string) => void;
    retrying: boolean;
    handleEditDialog: (open: boolean, image: Image, adCreative: AdCreative) => void;
    handleEditDialogV2: (open: boolean, ad: AdCreative) => void;
    currentImageIndexByCreativeId: Record<string, number>;
    setCurrentImageIndexByCreativeId: React.Dispatch<
      React.SetStateAction<Record<string, number>>
    >;
    onCopyPrompt?: (data: PromptCopyData) => void;
  }) => {
    const creativePrompts = workflow.creative_prompts || [];
    // While the workflow is still pending, we avoid per-ad polling to prevent request explosions.
    // Workflow polling will keep the nested creatives updated.
    const enableAdCreativePolling = workflow.status !== "pending";
    const config = workflow.config;

    const competitorConfig =
      config &&
      typeof config === "object" &&
      config !== null &&
      "competitor_ads" in config
        ? (config as { competitor_ads?: unknown }).competitor_ads
        : undefined;
    const ownConfig =
      config && typeof config === "object" && config !== null && "own_ads" in config
        ? (config as { own_ads?: unknown }).own_ads
        : undefined;

    const competitorSortBy =
      competitorConfig &&
      typeof competitorConfig === "object" &&
      competitorConfig !== null &&
      "sort_by" in competitorConfig
        ? (competitorConfig as { sort_by?: unknown }).sort_by
        : undefined;
    const competitorLocationKey =
      competitorConfig &&
      typeof competitorConfig === "object" &&
      competitorConfig !== null &&
      "location_key" in competitorConfig
        ? (competitorConfig as { location_key?: unknown }).location_key
        : undefined;

    const ownDateRange =
      ownConfig &&
      typeof ownConfig === "object" &&
      ownConfig !== null &&
      "date_range" in ownConfig
        ? (ownConfig as { date_range?: unknown }).date_range
        : undefined;

    const formatDateRange = (value: unknown): string | null => {
      if (!value) return null;
      if (typeof value === "string") return value;
      if (typeof value !== "object") return null;
      const obj = value as Record<string, unknown>;
      const start =
        (typeof obj.start_date === "string" && obj.start_date) ||
        (typeof obj.start === "string" && obj.start) ||
        (typeof obj.from === "string" && obj.from) ||
        null;
      const end =
        (typeof obj.end_date === "string" && obj.end_date) ||
        (typeof obj.end === "string" && obj.end) ||
        (typeof obj.to === "string" && obj.to) ||
        null;
      if (start && end) return `${start} to ${end}`;
      return null;
    };

    const competitorBrands =
      competitorConfig &&
      typeof competitorConfig === "object" &&
      competitorConfig !== null &&
      "brands" in competitorConfig
        ? (competitorConfig as { brands?: unknown }).brands
        : undefined;

    const formatTagValue = (value: unknown): string => {
      if (value === null || value === undefined) return "";
      let s = "";
      if (typeof value === "string") s = value;
      else if (typeof value === "number") s = String(value);
      else if (typeof value === "boolean") s = value ? "True" : "False";
      else if (typeof value === "bigint") s = String(value);
      else return "";
      s = s.trim();
      if (!s) return "";
      // If it's already something like "4:5" or "2025-12-13 to 2025-01-29", keep as-is.
      if (!s.includes("_")) {
        // Normalize plain lowercase tokens like "all" -> "All"
        if (/^[a-z]+$/.test(s)) return s[0].toUpperCase() + s.slice(1);
        return s;
      }
      return s
        .split("_")
        .filter(Boolean)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
        .join(" ");
    };

    const tags: Array<{
      key: string;
      icon?: React.ReactNode;
      label: React.ReactNode;
    }> = [];

    if (workflow.ad_type) {
      tags.push({
        key: "ad_type",
        icon: <RiLandscapeAiLine size={14} className="text-brand-primary-400" />,
        label: formatTagValue(workflow.ad_type),
      });
    }
    if (workflow.aspect_ratio) {
      tags.push({
        key: "aspect_ratio",
        icon: <RiAspectRatioLine size={14} className="text-brand-primary-400" />,
        label: String(workflow.aspect_ratio),
      });
    }
    if (typeof workflow.number_of_ads === "number") {
      tags.push({
        key: "number_of_ads",
        icon: <RiApps2AiLine size={14} className="text-brand-primary-400" />,
        label: String(workflow.number_of_ads),
      });
    }

    if (workflow.inspiration_source === "own_ads") {
      tags.push({
        key: "inspiration_source",
        icon: <RiHome3Line size={14} className="text-brand-primary-400" />,
        label: "Own Account",
      });
      const dr = formatDateRange(ownDateRange);
      if (dr) {
        tags.push({
          key: "date_range",
          icon: <RiCalendarLine size={14} className="text-brand-primary-400" />,
          label: dr,
        });
      }
    } else if (workflow.inspiration_source === "competitor_ads") {
      tags.push({
        key: "inspiration_source",
        icon: <RiMegaphoneLine size={14} className="text-brand-primary-400" />,
        label: "Competitors",
      });
      // Show all competitors in a single tag (avatar + name) when present in config.competitor_ads.brands
      if (Array.isArray(competitorBrands)) {
        const brands = competitorBrands
          .filter(
            (b): b is Record<string, unknown> => typeof b === "object" && b !== null,
          )
          .map((b) => ({
            id: typeof b.id === "string" ? b.id : "",
            name: typeof b.name === "string" ? b.name : "",
            profile_image_url:
              typeof b.profile_image_url === "string" ? b.profile_image_url : "",
          }))
          .filter((b) => b.name);

        if (brands.length > 0) {
          tags.push({
            key: "competitor_brands",
            label: (
              <span className="inline-flex flex-wrap items-center gap-2 align-middle">
                {brands.map((b) => (
                  <span key={b.id || b.name} className="inline-flex items-center gap-1.5">
                    {b.profile_image_url ? (
                      <img
                        src={b.profile_image_url}
                        alt={b.name}
                        className="h-4 w-4 rounded-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="h-4 w-4 rounded-full bg-[#E8E8E2]" />
                    )}
                    <span className="text-[12px] leading-[14px]">{b.name}</span>
                  </span>
                ))}
              </span>
            ),
          });
        }
      }
      if (typeof competitorSortBy === "string" && competitorSortBy) {
        tags.push({
          key: "competitor_sort_by",
          icon: <RiSortAlphabetDesc size={14} className="text-brand-primary-400" />,
          label: formatTagValue(competitorSortBy),
        });
      }
      if (typeof competitorLocationKey === "string" && competitorLocationKey) {
        tags.push({
          key: "competitor_location_key",
          icon: <RiMapPinLine size={14} className="text-brand-primary-400" />,
          label: formatTagValue(competitorLocationKey),
        });
      }
    }

    return (
      <div className="flex flex-col w-full">
        {/* Workflow Prompt */}
        <div className="w-full flex gap-3">
          <span className="bg-[#fff] rounded-[8px] p-1 flex items-center justify-center h-8 w-8 flex-shrink-0 border-[1px] border-border-gray shadow-sm">
            <Network size={14} className="shrink-0" color="#021859" strokeWidth={1.2} />
          </span>
          <div className="flex flex-col gap-1 p-0 w-full overflow-hidden pt-1">
            <span className="font-normal text-[14px] text-brand-primary-800 break-words whitespace-pre-wrap">
              {workflow.prompt}
            </span>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((t) => (
                  <span
                    key={t.key}
                    className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-[6px] border border-border-gray bg-white px-2 py-1 text-[12px] font-normal leading-[14px] tracking-[0.02em] text-brand-primary-800"
                  >
                    {t.icon ? <span className="shrink-0">{t.icon}</span> : null}
                    <span className="min-w-0">{t.label}</span>
                  </span>
                ))}
              </div>
            )}
            {workflow.status === "pending" && (
              <span className="text-brand-primary-400 text-[12px] font-normal leading-[16px] tracking-[0.02em]">
                {workflow.progress_message ||
                  (workflow.progress
                    ? `In progress • ${workflow.progress.progress_message} • ${workflow.progress.pct}%`
                    : "In progress")}
              </span>
            )}
            {workflow.status === "failed" && workflow.error_message && (
              <div className="flex flex-col gap-2 mt-1">
                <span className="text-[#E7574E] text-[12px] font-normal leading-[16px] tracking-[0.02em]">
                  {workflow.error_message}
                </span>
                <div>
                  <Button
                    variant="sq-outline"
                    className="h-7 px-3 text-[12px]"
                    disabled={retrying}
                    onClick={() => onRetry(workflow.id)}
                  >
                    {retrying ? "Retrying..." : "Retry"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workflow Creative Prompts */}
        <div className="ml-[44px] mt-4 flex flex-col gap-6 border-l border-border-gray pl-4">
          {creativePrompts.map((cp, idx) => (
            <RenderWorkflowPrompt
              key={cp.id}
              workflowId={workflow.id}
              enableAdCreativePolling={enableAdCreativePolling}
              promptItem={cp}
              promptIndex={idx}
              totalPrompts={creativePrompts.length}
              onBookmark={onBookmark}
              onCreativeUpdate={onCreativeUpdate}
              handleEditDialog={handleEditDialog}
              handleEditDialogV2={handleEditDialogV2}
              currentImageIndexByCreativeId={currentImageIndexByCreativeId}
              setCurrentImageIndexByCreativeId={setCurrentImageIndexByCreativeId}
              onCopyPrompt={onCopyPrompt}
            />
          ))}
        </div>

        {workflowIndex !== totalWorkflows - 1 && <Separator className="mt-6" />}
      </div>
    );
  },
);

// Display name for debugging
RenderWorkflow.displayName = "RenderWorkflow";

const RenderWorkflowPrompt = memo(
  ({
    workflowId,
    enableAdCreativePolling,
    promptItem,
    promptIndex,
    totalPrompts,
    onBookmark,
    onCreativeUpdate,
    handleEditDialog,
    handleEditDialogV2,
    currentImageIndexByCreativeId,
    setCurrentImageIndexByCreativeId,
    onCopyPrompt,
  }: {
    workflowId: string;
    enableAdCreativePolling: boolean;
    promptItem: WorkflowCreativePrompt;
    promptIndex: number;
    totalPrompts: number;
    onBookmark: (
      folderId: string | null,
      workflowId: string,
      creativePromptId: string,
      creativeId: string,
    ) => void;
    onCreativeUpdate: (updated: AdCreative) => void;
    handleEditDialog: (open: boolean, image: Image, adCreative: AdCreative) => void;
    handleEditDialogV2: (open: boolean, ad: AdCreative) => void;
    currentImageIndexByCreativeId: Record<string, number>;
    setCurrentImageIndexByCreativeId: React.Dispatch<
      React.SetStateAction<Record<string, number>>
    >;
    onCopyPrompt?: (data: PromptCopyData) => void;
  }) => {
    const failed = promptItem.ad_creatives.some(
      (adCreative) => adCreative.status === "failed",
    );
    const allFailed = promptItem.ad_creatives.every(
      (adCreative) => adCreative.status === "failed",
    );
    const { selectedAccount } = useGlobalStore();

    // Memoize the poll complete callback to prevent unnecessary re-renders
    const onSinglePollComplete = useCallback(
      (adCreative: AdCreative) => {
        onCreativeUpdate(adCreative);
      },
      [onCreativeUpdate],
    );

    return (
      <div className="flex flex-col w-full">
        {/* Creative Prompt */}
        <div className="w-full flex gap-3">
          <PromptCopyButton data={{ prompt: promptItem.prompt, files: promptItem.files, libraryFiles: promptItem.library_files }} onCopy={onCopyPrompt} />
          <div className="flex flex-col gap-2 p-0 w-full overflow-hidden pt-1">
            <span className="font-normal text-[14px] text-brand-primary-800 break-words whitespace-pre-wrap">
              {promptItem.prompt}
            </span>
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto scrollbar-hide py-2">
              {promptItem.files.length > 0 && (
                <FileAttachmentsPreview
                  files={promptItem.files}
                  className="!overflow-visible py-0 flex-none"
                />
              )}
              {promptItem.files.length > 0 && promptItem.library_files.length > 0 && (
                <Separator
                  orientation="vertical"
                  className="h-[75px] bg-[#D4D4D4] shrink-0 mx-1"
                />
              )}
              {promptItem.library_files.length > 0 && (
                <LibraryAttachmentsPreview
                  attachments={promptItem.library_files}
                  className="!overflow-visible py-0 flex-none"
                />
              )}
            </div>
          </div>
        </div>

        {/* Response Ad Previews */}
        <div
          className={`flex gap-2 ${allFailed ? "py-2" : "py-4"} overflow-x-auto scrollbar-hide`}
        >
          {promptItem.ad_creatives.map((adCreative: AdCreative) => {
            const availableImages = adCreative.creative?.images || [];
            const hasMultipleImages = availableImages.length > 1;
            const currentImageIndex = currentImageIndexByCreativeId[adCreative.id] || 0;
            const currentImage = availableImages[currentImageIndex];

            const actions: Action[] = [
              {
                component: (
                  <DownloadAction
                    url={
                      adCreative.creative_type === "video"
                        ? adCreative.creative?.videos?.[0]?.permalink_url
                        : currentImage?.uncompressed_url || currentImage?.permalink_url
                    }
                    type={adCreative.creative_type}
                    aspectRatio={
                      adCreative.creative_type === "video"
                        ? adCreative.creative?.videos?.[0]?.aspect_ratio
                        : currentImage?.aspect_ratio
                    }
                  />
                ),
                position: "top-right",
                visibility: "hover",
              },
              {
                component: (
                  <BookmarkAction
                    folderId={adCreative.folder_id}
                    onBookmark={(folderId) => {
                      onBookmark(folderId, workflowId, promptItem.id, adCreative.id);
                    }}
                  />
                ),
                position: "top-right",
                visibility: "always",
              },
              ...(adCreative.creative_type === "image" && hasMultipleImages
                ? [
                    {
                      component: (
                        <ImageSwitcherAction
                          currentIndex={currentImageIndex}
                          totalImages={availableImages.length}
                          onPrevious={() => {
                            if (currentImageIndex > 0) {
                              setCurrentImageIndexByCreativeId((prev) => ({
                                ...prev,
                                [adCreative.id]: currentImageIndex - 1,
                              }));
                            }
                          }}
                          onNext={() => {
                            if (currentImageIndex < availableImages.length - 1) {
                              setCurrentImageIndexByCreativeId((prev) => ({
                                ...prev,
                                [adCreative.id]: currentImageIndex + 1,
                              }));
                            }
                          }}
                        />
                      ),
                      position: "top-left" as const,
                      visibility: "always-hover" as const,
                    },
                  ]
                : []),
              ...(adCreative.creative_type === "image"
                ? [
                    {
                      component: (
                        <EditImageAction
                          onClick={() => handleEditDialogV2(true, adCreative)}
                        />
                      ),
                      position: "bottom-right" as const,
                      visibility: "always-hover" as const,
                    },
                  ]
                : []),
            ];
            return (
              <div key={adCreative.id} className="max-w-[296px] flex-shrink-0">
                <PollingAdCard
                  accountIconUrl={selectedAccount?.additional_info?.ad_account_icon_url}
                  className="min-w-[296px] flex-shrink-0 p-2 rounded-[16px]"
                  adCreative={adCreative}
                  onCreativeUpdate={onCreativeUpdate}
                  enablePolling={enableAdCreativePolling}
                  mediaActions={actions}
                  onMediaClick={
                    adCreative.creative_type === "image"
                      ? () => handleEditDialog(true, currentImage, adCreative)
                      : undefined
                  }
                  onPollingComplete={onSinglePollComplete}
                  calculateHeight={true}
                  currentImageIndex={currentImageIndex}
                />
              </div>
            );
          })}
        </div>
        {failed && (
          <div className="flex gap-2 border border-solid border-[#FADDDA] bg-[#FDF6F5] rounded-[6px] p-2">
            <span className="p-3 bg-[#E54C4214] rounded-[4px] h-6 w-6 flex items-center justify-center">
              <Info size={16} className="shrink-0" color="#E54C42" />
            </span>
            <p className="text-[#E7574E] font-normal text-[14px] leading-[18px] tracking-[0.02em] w-[72%]">
              It looks like there was an issue with generating the image. Please check
              your connection and try again. If the problem continues, feel free to reach
              out to Getcrux team.
            </p>
          </div>
        )}

        <div className="text-brand-primary-400 text-[12px] font-normal leading-[18px] tracking-[0.02em] text-right self-end mt-4">
          {(() => {
            const date = new Date(promptItem.created_at);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const day = date.getDate();
            const month = date.toLocaleString("en-US", { month: "long" });
            const year = date.getFullYear();
            return `${hours % 12 || 12}:${minutes.toString().padStart(2, "0")}${hours >= 12 ? "PM" : "AM"}, ${day} ${month} ${year}`;
          })()}
        </div>

        {promptIndex !== totalPrompts - 1 && <Separator className="mt-6" />}
      </div>
    );
  },
);

// Display name for debugging
RenderWorkflowPrompt.displayName = "RenderWorkflowPrompt";

const EmptyState = (
  <div className="flex items-center justify-center flex-col">
    <svg
      width="305"
      height="305"
      viewBox="0 0 305 305"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M57.4056 145.711C52.1729 142.605 43.6957 144.834 39.6327 151.505C38.0505 154.114 37.1235 157.067 36.9312 160.112C31.6392 151.737 15.6445 159.601 18.4205 170.235C19.1734 173.034 20.4686 175.659 22.2325 177.959C19.6397 177.3 16.8995 177.547 14.4663 178.659C12.0331 179.771 10.0529 181.681 8.85412 184.073C8.05854 185.658 7.63261 187.403 7.60831 189.176C7.59123 190.364 7.86061 191.538 8.39363 192.599C8.92665 193.66 9.70761 194.577 10.6703 195.273C12.1605 196.339 13.826 197.136 15.5912 197.628C14.523 197.96 13.537 198.514 12.6979 199.254C11.8587 199.993 11.1854 200.902 10.7221 201.92C10.2592 202.939 10.0172 204.044 10.012 205.163C10.0067 206.282 10.2384 207.389 10.6916 208.412C12.9262 213.295 18.9499 214.864 23.8723 212.921C26.2984 211.966 28.484 210.488 30.2732 208.592C30.7371 200.849 31.7837 193.154 33.4037 185.568C36.128 173.825 44.6996 168.034 56.5675 165.945C58.5731 162.627 59.9543 158.97 60.6426 155.155C61.1309 151.686 60.705 147.672 57.4056 145.711ZM50.6731 163.025C49.3094 163.25 47.9113 162.942 46.7684 162.165C45.6256 161.388 44.8254 160.2 44.5338 158.849C44.2935 157.81 44.2965 156.729 44.5424 155.691C44.7883 154.653 45.2705 153.686 45.9515 152.865C46.7251 152.099 47.7107 151.582 48.7809 151.382C49.8512 151.181 50.957 151.307 51.9554 151.741C53.0345 152.156 53.9561 152.899 54.5901 153.866C55.2242 154.833 55.5386 155.974 55.489 157.129C55.4402 158.503 54.943 159.823 54.0734 160.887C53.2038 161.952 52.0095 162.703 50.6731 163.025Z"
        fill="#021859"
      />
      <path
        d="M260.646 138.757C259.554 135.724 257.432 133.17 254.651 131.54C252.061 129.802 249.064 128.764 245.953 128.528C240.835 128.174 236.936 130.037 233.147 132.984C226.238 138.356 212.809 147.171 201.036 163.624L201.285 163.686C202.68 161.905 204.588 160.597 206.752 159.938C223.607 153.028 241.706 149.666 259.919 150.063C261.413 146.48 261.668 142.501 260.646 138.757Z"
        fill="#ECEDEF"
      />
      <path
        d="M275.479 153.567C271.22 150.749 266.451 150.264 261.332 150.094C246.294 149.614 231.283 151.693 216.941 156.241C212.571 157.588 208.298 159.233 204.151 161.163C203.011 161.764 202.03 162.63 201.292 163.687C201.47 163.729 201.645 163.778 201.822 163.822C206.274 162.696 210.897 162.408 215.453 162.975C218.336 163.513 221.158 164.335 223.879 165.427C237.291 170.015 252.933 176.236 263.979 185.214C267.634 184.453 271.224 183.41 274.717 182.094C277.597 181.134 280.046 179.185 281.627 176.593C285.579 169.228 282.595 158.282 275.479 153.567Z"
        fill="#ECEDEF"
      />
      <path
        d="M120.79 162.472C77.2359 164.422 58.6263 165.058 56.731 165.917C56.8517 165.895 56.9761 165.919 57.0801 165.984C57.1841 166.049 57.2603 166.15 57.2938 166.268C58.3009 170.465 58.9462 174.739 59.2226 179.046C59.3536 181.134 59.3338 183.229 59.1633 185.315C59.1907 185.709 59.1359 186.106 59.0005 186.477C58.9465 186.559 58.8695 186.625 58.779 186.665C58.6885 186.704 58.5883 186.717 58.4908 186.701C58.3932 186.685 58.3025 186.641 58.2296 186.574C58.1567 186.507 58.1048 186.42 58.0803 186.325C58.0422 186.247 57.5266 185.316 57.4855 185.24C54.5391 179.867 50.899 175.088 47.0155 169.44C46.9513 169.328 46.9323 169.196 46.9626 169.071C46.9929 168.946 47.0701 168.837 47.1783 168.768L47.1388 168.778C43.6539 170.246 40.5806 172.544 38.1864 175.47C35.7921 178.397 34.1495 181.865 33.4014 185.572C30.207 199.368 28.9065 221.496 30.5113 236.438C30.842 239.792 31.7975 243.055 33.3284 246.058C36.8376 252.792 44.2516 254.867 51.6291 253.641C54.6021 253.176 57.5474 252.548 60.4517 251.761C60.3872 251.777 60.32 251.781 60.254 251.772C60.188 251.762 60.1245 251.74 60.0672 251.706C60.0098 251.672 59.9597 251.627 59.9197 251.574C59.8797 251.521 59.8506 251.46 59.8341 251.396C59.7976 251.251 59.8098 208.5 59.8189 208.176C59.8402 207.499 60.6418 207.233 61.153 206.907C80.0939 194.835 99.8786 184.142 120.354 174.908V162.971C120.357 162.845 120.406 162.726 120.492 162.635C120.578 162.543 120.695 162.487 120.819 162.476L120.79 162.472ZM39.4297 242.66C39.0068 242.174 39.7156 241.461 40.1385 241.951C41.2225 243.206 42.4411 244.338 43.7725 245.326C44.2866 245.707 43.7862 246.577 43.266 246.193C41.8609 245.16 40.5743 243.975 39.4297 242.66ZM45.4625 215.9C49.4258 213.841 53.3062 211.626 57.0946 209.26C57.6437 208.916 58.1472 209.785 57.6011 210.127C53.8126 212.493 49.9322 214.708 45.969 216.767C45.3955 217.067 44.889 216.198 45.4625 215.9ZM82.5112 191.547L80.4683 185.356C80.2645 184.74 81.2289 184.478 81.4357 185.089L83.4786 191.279C83.6824 191.895 82.7135 192.157 82.5112 191.547Z"
        fill="#FF4500"
      />
      <path
        d="M133.485 161.902C120.438 162.486 120.972 162.442 120.82 162.476C120.695 162.487 120.578 162.543 120.493 162.634C120.407 162.725 120.357 162.845 120.354 162.97V174.908C126.454 172.287 132.202 170.345 138.836 171.012C136.028 166.148 134.391 163.451 133.485 161.902Z"
        fill="white"
      />
      <path
        d="M232.615 184.736C232.512 184.003 232.264 183.298 231.885 182.663C231.505 182.028 231.003 181.475 230.406 181.038C224.373 175.791 217.553 171.524 210.197 168.392C203.047 165.198 201.623 164.988 201.374 164.742C201.305 164.684 201.254 164.609 201.225 164.524C201.196 164.44 201.191 164.348 201.211 164.261C201.23 164.174 201.271 164.093 201.332 164.028C201.393 163.962 201.471 163.915 201.556 163.89C201.645 163.866 201.734 163.847 201.823 163.826C183.569 159.349 167.065 160.402 148.315 161.24C151.545 166.371 154.827 172.328 157.644 177.749C164.777 181.203 171.815 184.85 178.75 188.684C180.985 189.735 182.869 191.407 184.179 193.5C184.626 194.352 184.813 195.315 184.718 196.273C184.623 197.23 184.25 198.138 183.645 198.885C181.335 201.714 176.091 202.21 172.444 202.212C172.596 202.516 172.748 202.811 172.891 203.114C179.056 203.298 185.204 203.838 191.307 204.735C195.192 205.472 199.138 205.834 203.091 205.818C202.171 202.829 201.963 199.666 202.483 196.582C202.706 195.68 203.24 194.885 203.991 194.338C204.743 193.791 205.663 193.526 206.59 193.59C207.552 193.678 208.481 193.984 209.308 194.485C210.134 194.986 210.835 195.668 211.359 196.48C213.049 199.115 214.436 201.933 215.493 204.88C218.726 204.424 221.931 203.78 225.088 202.954C216.847 197.387 208.931 191.354 201.377 184.885C201.312 184.854 201.256 184.808 201.211 184.752C201.167 184.695 201.137 184.63 201.122 184.559C201.107 184.489 201.109 184.417 201.127 184.347C201.145 184.278 201.178 184.213 201.225 184.159C201.287 184.086 201.37 184.033 201.462 184.008C201.745 183.865 200.368 183.808 224.045 187.142C225.893 187.515 227.782 187.656 229.665 187.559C230.586 187.512 231.46 187.14 232.133 186.511C232.329 186.263 232.472 185.977 232.555 185.672C232.638 185.367 232.658 185.049 232.615 184.736Z"
        fill="white"
      />
      <path
        d="M59.2231 179.038C58.9465 174.731 58.3012 170.456 57.2943 166.26C57.2567 166.134 57.1716 166.026 57.0567 165.961C56.9419 165.896 56.8063 165.878 56.6783 165.91C53.4398 166.458 50.2819 167.398 47.2716 168.711C47.2041 168.737 47.1431 168.778 47.0927 168.83C47.0423 168.882 47.0037 168.944 46.9794 169.012C46.9552 169.08 46.9459 169.153 46.9522 169.225C46.9586 169.297 46.9803 169.367 47.016 169.43C51.5277 176.02 54.246 179.318 58.0807 186.315C58.1056 186.41 58.1576 186.497 58.2305 186.563C58.3034 186.63 58.394 186.674 58.4915 186.691C58.5889 186.707 58.689 186.694 58.7795 186.654C58.8699 186.615 58.9469 186.55 59.001 186.467C59.1364 186.096 59.1912 185.699 59.1638 185.305C59.3342 183.219 59.3539 181.125 59.2231 179.038Z"
        fill="white"
      />
      <path
        d="M269.497 193.171C268.458 189.104 266.312 186.977 262.989 184.417C251.777 175.777 237.08 169.943 223.875 165.427C221.154 164.335 218.332 163.514 215.449 162.975C211.148 162.435 206.785 162.664 202.564 163.652C202.412 163.687 201.942 163.78 201.552 163.886C201.466 163.91 201.388 163.958 201.327 164.023C201.266 164.089 201.224 164.17 201.204 164.257C201.186 164.345 201.192 164.436 201.221 164.52C201.25 164.605 201.302 164.68 201.37 164.738C201.837 165.194 219.12 170.588 230.408 181.04C231.008 181.475 231.513 182.027 231.893 182.662C232.272 183.297 232.518 184.005 232.617 184.738C232.659 185.051 232.636 185.37 232.55 185.674C232.464 185.979 232.316 186.262 232.116 186.507C231.444 187.137 230.57 187.508 229.649 187.555C227.766 187.652 225.877 187.512 224.029 187.138C200.246 183.792 201.7 183.836 201.345 184.053C201.27 184.094 201.207 184.154 201.164 184.228C201.121 184.302 201.099 184.386 201.101 184.472C201.102 184.557 201.127 184.641 201.173 184.713C201.219 184.785 201.284 184.843 201.361 184.881C214.353 195.553 229.34 208.101 245.473 212.699C250.907 214.249 257.389 215.31 262.585 212.432C268.786 209.005 271.394 200.6 269.497 193.171Z"
        fill="#FF4500"
      />
      <path
        d="M142.024 152.25C141.982 152.189 141.979 152.209 141.947 152.148C141.107 150.951 140.008 149.96 138.731 149.249C137.453 148.537 136.032 148.125 134.572 148.041C133.247 147.971 131.946 148.416 130.941 149.282C129.935 150.148 129.303 151.369 129.177 152.69C129.179 154.586 129.744 156.439 130.801 158.014C130.869 158.129 130.979 158.212 131.107 158.246C131.236 158.279 131.372 158.26 131.487 158.193C134.808 156.193 138.269 154.436 141.845 152.938C141.902 152.905 141.952 152.86 141.991 152.808C142.031 152.755 142.06 152.695 142.077 152.631C142.094 152.567 142.098 152.501 142.089 152.436C142.079 152.37 142.058 152.307 142.024 152.25Z"
        fill="#FF4500"
      />
      <path
        d="M144.313 155.492C144.234 155.415 144.132 155.365 144.022 155.351C143.912 155.337 143.801 155.358 143.705 155.412C140.119 156.893 136.657 158.657 133.352 160.686C133.238 160.755 133.156 160.865 133.123 160.994C133.089 161.122 133.107 161.258 133.172 161.374C133.274 161.55 138.496 170.415 138.835 171.012C145.41 171.663 151.309 174.681 157.64 177.747C154.689 172.072 148.391 160.469 144.313 155.492Z"
        fill="#FF4500"
      />
      <path
        d="M184.692 228.655C184.145 227.427 180.871 220.061 179.749 217.619C177.861 213.512 172.59 202.508 172.438 202.21C169.065 202.224 165.705 201.788 162.447 200.914C162.142 203.233 161.658 205.525 160.999 207.77C165.803 216.311 171.119 224.555 176.918 232.455C177.628 233.459 178.432 234.394 179.314 235.251C180.608 236.457 182.797 237.499 184.531 236.542C186.913 235.229 185.814 231.172 184.692 228.655Z"
        fill="#FF4500"
      />
      <path
        d="M184.173 193.499C182.863 191.406 180.978 189.735 178.744 188.683C171.819 184.831 164.78 181.184 157.639 177.748C149.881 173.992 141.927 169.838 132.946 171.084C128.964 171.669 125.082 172.803 121.41 174.452C100.569 183.806 80.4301 194.652 61.15 206.905C60.6389 207.23 59.8373 207.497 59.816 208.174C59.7947 208.851 59.816 209.526 59.816 210.2V251.27C59.8175 251.347 59.8362 251.422 59.8707 251.491C59.9052 251.559 59.9546 251.619 60.0152 251.666C60.0764 251.712 60.147 251.744 60.222 251.759C60.297 251.774 60.3745 251.773 60.4488 251.754C90.9268 243.319 119.942 230.291 146.496 213.117C145.434 215.505 144.975 218.117 145.159 220.723C145.479 223.193 147.251 226.526 150.199 225.008C151.463 224.205 152.554 223.157 153.41 221.928C158.351 215.921 161.483 208.634 162.441 200.916C165.319 201.695 168.282 202.121 171.263 202.185C175.253 202.296 181.123 201.957 183.632 198.886C184.239 198.139 184.614 197.231 184.71 196.274C184.806 195.316 184.62 194.352 184.173 193.499ZM162.206 198.327C162.108 198.966 161.125 198.692 161.239 198.061C161.578 196.173 159.538 194.888 157.917 194.751C155.977 194.745 154.052 195.086 152.232 195.758C146.619 197.506 141.151 199.693 135.88 202.299C135.299 202.571 134.791 201.707 135.375 201.433C141.316 198.45 147.531 196.046 153.933 194.254C154.373 194.141 154.306 194.208 154.272 194.102C154.161 192.935 153.781 191.81 153.162 190.814C152.544 189.818 151.703 188.979 150.707 188.362C149.889 188.034 149.011 187.882 148.13 187.917C142.152 188.02 136.21 188.849 130.433 190.385C130.306 190.417 130.173 190.397 130.06 190.331C129.948 190.264 129.866 190.157 129.832 190.031C129.797 189.905 129.813 189.771 129.875 189.657C129.938 189.542 130.042 189.457 130.167 189.418C136.755 187.514 146.764 186.63 149.648 187.013C152.972 187.456 154.695 190.828 155.24 193.834C155.244 193.878 155.244 193.922 155.24 193.966C156.796 193.536 158.453 193.647 159.938 194.28C160.709 194.611 161.349 195.187 161.759 195.919C162.169 196.651 162.326 197.497 162.206 198.327Z"
        fill="white"
      />
      <path
        d="M185.978 238.915C185.712 238.327 184.847 238.837 185.111 239.421L187.043 243.689C187.309 244.277 188.173 243.767 187.909 243.183L185.978 238.915Z"
        fill="#ECEDEF"
      />
      <path
        d="M51.9549 151.743C50.9565 151.309 49.8507 151.184 48.7804 151.384C47.7101 151.584 46.7245 152.101 45.951 152.867C45.2703 153.689 44.7882 154.656 44.5423 155.694C44.2964 156.732 44.2933 157.812 44.5333 158.852C44.8252 160.202 45.6254 161.389 46.7682 162.167C47.911 162.944 49.3089 163.252 50.6726 163.027C52.0087 162.705 53.2027 161.954 54.0723 160.889C54.9418 159.825 55.4392 158.505 55.4885 157.131C55.5371 155.976 55.2223 154.835 54.5884 153.869C53.9545 152.902 53.0334 152.159 51.9549 151.743Z"
        fill="white"
      />
      <path
        d="M228.583 247.914C228.486 246.678 228.076 245.486 227.393 244.452C226.814 243.701 225.975 243.194 225.041 243.029C224.129 242.863 223.484 242.807 213.776 243.638C203.861 244.491 193.618 245.734 183.81 247.171C184.347 248.47 184.614 249.864 184.595 251.269C184.593 251.358 184.569 251.444 184.524 251.52C184.479 251.597 184.416 251.66 184.339 251.704C184.262 251.748 184.175 251.771 184.086 251.771C183.998 251.77 183.911 251.747 183.834 251.703C182.465 250.722 178.714 248.052 178.575 247.961C176.695 248.248 138.824 254.75 137.368 255.043C136.492 255.216 135.707 255.699 135.159 256.404C134.61 257.109 134.335 257.988 134.383 258.88C134.386 259.012 134.439 259.138 134.533 259.232C134.627 259.325 134.753 259.379 134.885 259.381H228.032C228.164 259.378 228.289 259.324 228.383 259.231C228.476 259.137 228.529 259.011 228.532 258.88C228.896 255.233 228.913 251.563 228.583 247.914Z"
        fill="#ECEDEF"
      />
      <path
        d="M181.826 132.569C181.131 131.079 180.087 129.779 178.784 128.777C176.872 127.472 173.916 127.189 172.547 129.407C171.078 131.78 172.263 134.998 173.177 137.349C173.869 139.075 174.679 140.753 175.6 142.368C176.3 143.662 177.235 144.813 178.358 145.764C178.915 146.224 179.609 146.488 180.331 146.517C181.053 146.546 181.765 146.336 182.357 145.922C182.75 145.647 183.078 145.291 183.32 144.877C185.323 141.337 183.851 136.075 181.826 132.569Z"
        fill="#FF4500"
      />
      <path
        d="M147.901 117.27C147.316 113.1 146.404 108.984 145.172 104.958C144.697 103.255 143.985 101.628 143.057 100.123C142.386 99.1848 141.286 98.4045 140.245 99.34C139.344 100.333 138.855 101.632 138.876 102.972C138.775 107.349 139.278 111.719 140.375 115.955C140.794 117.882 141.533 119.725 142.561 121.407C143.638 123.096 145.252 123.876 146.849 122.654C146.864 122.639 146.879 122.624 146.891 122.607C146.912 122.598 146.932 122.588 146.951 122.575C148.45 121.36 148.128 118.979 147.901 117.27Z"
        fill="#FF4500"
      />
      <path
        d="M152.258 68.2489C151.55 65.088 151.213 57.2298 147.095 57.6131C145.708 57.7424 145.191 58.9046 145.102 60.1489C144.854 63.6657 145.572 71.8829 148.691 73.5714C151.274 74.9678 153.154 71.3627 152.258 68.2489Z"
        fill="#FF4500"
      />
      <path
        d="M81.1507 63.3149C80.5181 58.9453 79.0928 54.7279 76.9447 50.8705C76.4167 49.6124 75.5246 48.5408 74.3832 47.7933C74.0771 47.6014 73.7296 47.4855 73.3696 47.4553C73.0097 47.425 72.6477 47.4811 72.3138 47.6191C71.98 47.7571 71.684 47.9728 71.4504 48.2484C71.2169 48.524 71.0526 48.8514 70.9713 49.2034C70.7049 50.3934 70.6396 51.6197 70.7781 52.8313C71.3959 58.7156 72.7607 64.4973 74.8395 70.0368C75.1825 70.5541 75.6371 70.9878 76.17 71.3061C76.7028 71.6244 77.3002 71.8191 77.9183 71.8758C78.4585 71.8918 78.9934 71.766 79.4698 71.5108C79.9462 71.2557 80.3474 70.8802 80.6335 70.4216C81.9143 68.3879 81.464 65.5419 81.1507 63.3149Z"
        fill="#FF4500"
      />
      <path
        d="M169.285 106.133C166.975 107.037 166.911 110.368 168.403 112.466C169.021 113.336 170.285 113.354 170.828 111.401C171.348 109.538 171.149 105.628 169.285 106.133Z"
        fill="#FF4500"
      />
      <path
        d="M218.358 215.284C217.222 208.63 214.841 202.249 211.341 196.476C210.818 195.663 210.117 194.981 209.29 194.48C208.464 193.979 207.535 193.673 206.572 193.585C205.645 193.522 204.725 193.787 203.974 194.334C203.223 194.881 202.689 195.676 202.465 196.578C202.222 197.572 202.12 198.597 202.161 199.62C202.213 203.115 203.022 206.556 204.534 209.708C205.807 212.466 207.561 215.829 205.737 218.28C203.996 220.619 200.526 220.257 199.244 223.851C198.988 225.166 199.104 226.525 199.578 227.778C200.273 230.135 202.164 233.993 204.545 234.575C205.169 234.807 205.852 234.832 206.491 234.647C207.131 234.462 207.694 234.075 208.098 233.546C208.565 232.446 208.705 231.236 208.501 230.059C208.298 228.664 208.41 227.242 208.83 225.896C209.033 225.467 209.337 225.094 209.715 224.807C210.093 224.521 210.534 224.329 211.002 224.248C213.874 223.453 216.886 223.336 217.961 220.231C218.495 218.639 218.632 216.941 218.358 215.284Z"
        fill="#FF4500"
      />
      <path
        d="M181.739 243.219C179.004 239.162 175.838 235.412 172.296 232.036C170.795 230.437 169.022 229.118 167.059 228.141C165.405 227.236 163.496 226.908 161.634 227.207C160.809 227.406 160.073 227.874 159.543 228.537C159.013 229.2 158.718 230.021 158.706 230.87C158.854 232.462 159.622 233.933 160.843 234.964C162.876 236.964 163.019 236.718 183.843 251.707C183.92 251.751 184.007 251.775 184.095 251.775C184.184 251.775 184.271 251.753 184.348 251.709C184.425 251.665 184.489 251.602 184.534 251.525C184.579 251.449 184.603 251.362 184.604 251.273C184.462 248.365 183.466 245.563 181.739 243.219Z"
        fill="#FF4500"
      />
      <path
        d="M158.727 244.455C155.82 240.994 152.458 238.261 148.03 237.587C146.95 237.423 143.503 236.989 143.707 238.947C143.799 239.817 144.743 240.371 145.469 240.67C147.054 241.321 148.674 241.516 154.561 243.9C155.777 244.394 156.994 244.859 158.239 245.293C158.748 245.471 159.022 244.806 158.727 244.455Z"
        fill="#FF4500"
      />
      <path
        d="M295.992 149.049L283.324 151.13C282.688 151.233 282.957 152.194 283.59 152.097L296.258 150.016C296.894 149.913 296.621 148.945 295.992 149.049Z"
        fill="#021859"
      />
      <path
        d="M281.042 200.471C280.513 200.098 280.012 200.968 280.537 201.336L291.529 209.065C292.058 209.438 292.56 208.568 292.035 208.198L281.042 200.471Z"
        fill="#021859"
      />
      <path
        d="M254.47 239.142C254.499 239.235 254.554 239.318 254.628 239.381C254.703 239.443 254.794 239.482 254.891 239.494C254.988 239.505 255.086 239.488 255.173 239.444C255.26 239.401 255.333 239.333 255.382 239.249C255.448 239.136 255.468 239.003 255.438 238.876C254.483 235.126 252.129 226.596 251.974 226.062C251.946 225.969 251.891 225.886 251.816 225.823C251.742 225.761 251.65 225.722 251.554 225.71C251.457 225.699 251.359 225.716 251.272 225.76C251.185 225.803 251.112 225.871 251.063 225.955C250.996 226.068 250.976 226.201 251.007 226.328C251.962 230.076 254.315 238.608 254.47 239.142Z"
        fill="#021859"
      />
      <path
        d="M265.483 125.303L273.312 116.559C273.743 116.079 273.035 115.367 272.603 115.849L264.774 124.594C264.344 125.073 265.051 125.785 265.483 125.303Z"
        fill="#021859"
      />
      <path
        d="M228.846 122.853C228.914 122.967 229.024 123.05 229.152 123.086C229.281 123.119 229.417 123.099 229.532 123.032C229.647 122.965 229.73 122.855 229.765 122.727C229.782 122.663 229.786 122.597 229.777 122.531C229.768 122.466 229.746 122.403 229.713 122.346C228.022 119.367 223.708 112.261 223.708 112.261C223.64 112.146 223.529 112.063 223.4 112.03C223.271 111.996 223.134 112.016 223.019 112.083C222.904 112.151 222.82 112.262 222.787 112.391C222.753 112.52 222.773 112.657 222.841 112.772C224.543 115.768 225.831 117.938 228.846 122.853Z"
        fill="#021859"
      />
      <path
        d="M10.6684 195.274C12.1607 196.336 13.8255 197.133 15.5893 197.628C14.5951 197.936 13.6712 198.437 12.8705 199.102C12.0698 199.766 11.4078 200.583 10.9225 201.503C10.4372 202.424 10.1379 203.431 10.0419 204.467C9.94581 205.504 10.0548 206.549 10.3627 207.543C12.0055 212.815 18.4399 215.082 23.8703 212.922C26.2793 211.968 28.46 210.517 30.2697 208.663C29.565 217.909 29.6449 227.199 30.5086 236.432C30.8479 239.785 31.803 243.046 33.3257 246.052C36.9597 252.836 44.2474 254.859 51.6249 253.635C54.5969 253.165 57.5419 252.537 60.4475 251.755C80.1723 246.324 99.3026 238.929 117.551 229.679C123.543 233.879 129.133 238.624 134.251 243.854C137.185 246.981 139.707 250.471 141.754 254.239C140.29 254.5 138.824 254.748 137.37 255.043C136.494 255.216 135.709 255.699 135.161 256.404C134.613 257.109 134.337 257.988 134.385 258.88C134.388 259.012 134.442 259.138 134.535 259.232C134.629 259.325 134.755 259.379 134.887 259.382H228.034C228.166 259.379 228.292 259.325 228.385 259.232C228.478 259.138 228.532 259.012 228.534 258.88C228.917 255.234 228.933 251.559 228.577 247.911C228.488 246.673 228.079 245.48 227.389 244.449C226.804 243.703 225.968 243.196 225.037 243.022C225.623 241.271 226.254 239.532 226.756 237.759C230.833 224.021 223.136 214.79 218.39 204.402C220.638 204.023 222.867 203.537 225.069 202.946L225.051 202.936C234.798 209.215 252.452 218.036 262.58 212.427C270.338 208.133 272.22 196.083 267.729 189.095C266.693 187.607 265.424 186.295 263.97 185.21C267.627 184.457 271.217 183.413 274.708 182.09C277.579 181.113 280.021 179.168 281.617 176.59C285.601 169.179 282.585 158.272 275.469 153.563C270.808 150.478 265.507 150.184 259.917 150.064C261.438 146.487 261.694 142.499 260.642 138.757C259.557 135.721 257.434 133.166 254.649 131.541C252.13 129.807 249.194 128.777 246.144 128.556C243.094 128.336 240.04 128.933 237.297 130.286C234.844 131.562 234.103 132.273 228.942 136.161C225.106 139.063 221.456 141.871 217.892 145.031C213.232 139.209 208.014 136.346 203.629 130.987C195.831 121.461 199.73 110.33 201.405 99.0285C202.295 93.6391 202.637 88.1737 202.427 82.7158C201.522 59.8121 183.6 46.9768 160.596 43.7535C132.9 40.4182 104.839 45.4155 79.9971 58.107C79.2578 55.585 78.2341 53.1554 76.9457 50.8648C76.4161 49.6088 75.5243 48.5391 74.3841 47.7922C74.078 47.6003 73.7305 47.4844 73.3706 47.4541C73.0106 47.4238 72.6486 47.48 72.3148 47.618C71.9809 47.756 71.6849 47.9717 71.4514 48.2473C71.2178 48.5229 71.0536 48.8503 70.9722 49.2022C70.7014 50.3917 70.6361 51.6187 70.779 52.8301C71.0743 56.1107 71.654 59.3594 72.5116 62.5395C58.3651 71.9295 52.2501 83.9556 51.8029 94.6917C51.3891 104.64 56.529 113.213 63.6069 119.804C78.6661 133.826 101.229 138.608 102.158 163.309C79.9925 164.305 63.0243 164.804 56.5625 165.943C58.5687 162.627 59.9504 158.972 60.6391 155.158C61.1259 151.692 60.7 147.677 57.4022 145.717C55.8822 144.921 54.2101 144.458 52.4972 144.36C50.7844 144.262 49.0702 144.53 47.4692 145.146C44.4316 146.324 41.8102 148.374 39.9345 151.038C38.0589 153.701 37.0128 156.86 36.9277 160.117C31.6783 151.81 15.7688 159.442 18.4155 170.24C19.1896 173.032 20.4833 175.653 22.229 177.965C10.4965 175.058 3.11752 189.77 10.6684 195.274ZM60.8262 250.611C60.8262 250.611 60.8719 208.8 60.8491 208.294C78.6444 196.895 97.188 186.708 116.354 177.803C120.113 175.949 124.011 174.39 128.012 173.141C134.887 171.012 142.064 171.197 152.122 176.22C160.944 180.3 169.898 184.93 178.238 189.548C181.037 191.112 185.892 194.706 182.833 198.29C181.536 199.566 179.87 200.403 178.072 200.683C172.772 201.589 167.336 201.291 162.167 199.81C162.093 199.79 162.015 199.788 161.939 199.803C161.864 199.818 161.793 199.851 161.732 199.898C161.671 199.944 161.62 200.004 161.586 200.072C161.551 200.141 161.532 200.216 161.531 200.293C160.602 208.456 157.197 216.139 151.773 222.31C150.964 223.223 149.618 224.834 148.209 224.314C147.015 223.872 146.46 222.202 146.232 221.075C145.623 218.008 146.88 214.8 148.008 212.003C148.173 211.594 147.618 211.212 147.271 211.437C120.628 228.839 91.4737 242.05 60.8247 250.611H60.8262ZM138.835 171.012C132.891 170.403 126.843 172.111 121.357 174.474C121.357 164.468 121.386 163.369 121.396 163.447L134.061 162.88C135.309 164.995 138.492 170.415 138.835 171.012ZM134.297 161.291C137.34 159.449 140.516 157.834 143.798 156.46C147.639 161.263 153.253 171.55 156.119 177.006C150.836 174.496 145.871 172.009 140.086 171.168C135.067 162.445 135.873 163.885 134.297 161.291ZM183.449 228.294C183.986 229.437 184.417 230.628 184.733 231.851C184.966 232.693 185.005 233.577 184.849 234.437C184.804 234.709 184.698 234.968 184.537 235.193C184.377 235.418 184.168 235.604 183.925 235.736C183.23 235.978 182.469 235.947 181.796 235.651C180.414 235 179.236 233.985 178.389 232.715C172.132 224.325 166.453 215.52 161.39 206.36C161.864 204.572 162.22 202.756 162.455 200.922C165.334 201.697 168.296 202.123 171.277 202.19C173.086 205.712 178.341 216.664 183.449 228.294ZM227.176 246.159C227.374 246.727 227.51 247.316 227.582 247.914C227.927 251.394 227.927 254.9 227.582 258.381C227.582 258.381 136.504 258.323 135.426 258.367C135.479 257.896 135.655 257.446 135.936 257.064C136.218 256.683 136.595 256.382 137.03 256.192C137.73 255.963 138.451 255.81 139.183 255.736C139.997 255.583 176.746 249.239 179.739 248.787C181.108 249.759 182.477 250.728 183.846 251.709C183.903 251.743 183.967 251.765 184.033 251.774C184.098 251.784 184.165 251.78 184.229 251.763C184.294 251.746 184.354 251.717 184.407 251.676C184.459 251.636 184.504 251.586 184.537 251.528C184.582 251.451 184.606 251.364 184.607 251.276C184.633 250.215 184.482 249.157 184.16 248.147C206.064 244.974 214.34 244.706 219.827 244.175C221.366 243.987 222.916 243.91 224.465 243.945C225.09 243.988 225.687 244.224 226.172 244.621C226.657 245.017 227.007 245.555 227.174 246.159H227.176ZM163.36 235.836C161.426 234.376 158.174 231.352 160.575 228.912C161.985 227.481 164.377 228.113 165.996 228.778C168.11 229.706 170.013 231.055 171.588 232.745C175.745 237.004 182.818 244.142 183.556 250.273C179.038 247.032 166.06 237.874 163.358 235.836H163.36ZM225.27 221.107C228.219 229.017 226.897 235.057 224.029 242.926C222.521 242.9 220.509 243.116 218.904 243.23C207.272 244.082 195.326 245.485 183.814 247.172C181.461 241.405 176.209 236.02 172.298 232.032C170.797 230.433 169.024 229.114 167.061 228.138C165.405 227.237 163.498 226.909 161.636 227.204C160.811 227.403 160.075 227.871 159.545 228.534C159.015 229.197 158.721 230.018 158.708 230.867C158.697 233.825 162.388 236.448 164.966 238.223C166.849 239.52 175.901 246.054 178.586 247.958C174.928 248.516 147.26 253.32 142.827 254.062C140.695 250.095 138.053 246.423 134.969 243.141C129.935 237.993 124.442 233.314 118.559 229.163C128.139 224.289 137.465 218.932 146.501 213.11C145.439 215.499 144.98 218.11 145.162 220.716C145.482 223.186 147.255 226.519 150.203 225.001C151.468 224.198 152.559 223.151 153.415 221.921C156.848 217.746 159.424 212.935 160.997 207.764C165.799 216.307 171.115 224.551 176.916 232.45C177.626 233.454 178.43 234.39 179.313 235.246C180.606 236.452 182.795 237.494 184.529 236.539C186.867 235.249 185.821 231.108 184.691 228.65C181.393 220.999 176.519 210.642 172.884 203.107C179.047 203.304 185.196 203.845 191.3 204.729C195.185 205.468 199.131 205.832 203.086 205.812C204.4 210.394 206.403 212.493 206.519 215.61C206.573 216.562 206.297 217.504 205.737 218.276C203.956 220.607 200.519 220.276 199.243 223.848C198.988 225.162 199.103 226.522 199.578 227.774C200.27 230.12 202.178 233.991 204.544 234.572C205.169 234.804 205.851 234.829 206.491 234.644C207.131 234.458 207.694 234.072 208.098 233.542C208.563 232.443 208.703 231.233 208.501 230.056C208.297 228.661 208.409 227.238 208.829 225.892C209.033 225.464 209.337 225.091 209.715 224.804C210.093 224.517 210.534 224.326 211.002 224.245C214.035 223.405 216.843 223.332 217.961 220.228C218.491 218.635 218.627 216.937 218.356 215.28C217.772 211.716 216.803 208.225 215.466 204.87C216.087 204.779 216.704 204.673 217.323 204.566C219.835 210.141 223.221 215.439 225.265 221.107H225.268H225.27ZM206.723 233.527C206.188 233.734 205.605 233.783 205.044 233.666C204.482 233.549 203.967 233.273 203.559 232.87C201.648 230.824 200.44 228.223 200.11 225.444C199.706 221.489 204.455 221.652 206.599 218.787C209.152 215.378 206.031 210.991 204.747 207.707C203.541 204.658 203.015 201.381 203.206 198.107C203.244 197.177 203.562 196.279 204.118 195.532C204.746 194.96 205.553 194.625 206.401 194.585C207.249 194.546 208.085 194.804 208.762 195.315C210.331 196.571 211.543 198.216 212.279 200.086C214.24 204.095 215.765 208.303 216.828 212.637C217.26 214.335 217.468 216.081 217.444 217.832C217.442 218.552 217.291 219.264 217.001 219.924C216.711 220.583 216.288 221.175 215.758 221.664C212.923 223.375 211.613 222.531 209.118 223.982C205.81 226.329 208.828 232.465 206.723 233.527ZM215.089 203.909C214.113 201.301 212.852 198.808 211.33 196.475C210.81 195.66 210.109 194.976 209.282 194.475C208.456 193.974 207.525 193.669 206.561 193.585C205.634 193.521 204.714 193.786 203.963 194.334C203.212 194.881 202.678 195.675 202.454 196.577C201.981 199.316 202.096 202.124 202.791 204.816C193.799 204.881 190.413 202.779 173.751 202.169C177.048 202.078 181.522 201.446 183.623 198.88C184.231 198.134 184.606 197.226 184.701 196.268C184.796 195.311 184.607 194.346 184.157 193.495C182.853 191.397 180.966 189.724 178.728 188.679C169.324 183.474 159.977 178.874 157.616 177.727C155.616 173.789 152.119 167.443 148.895 162.212C166.933 161.31 180.34 160.738 195.906 163.608C198.331 163.888 200.71 164.481 202.982 165.376C204.264 165.844 206.467 166.698 210.18 168.392C216.006 170.941 221.534 174.124 226.663 177.884C228.432 179.207 230.087 180.675 231.613 182.272C232.159 182.989 232.5 183.841 232.599 184.735C232.642 185.048 232.62 185.367 232.534 185.671C232.448 185.976 232.299 186.259 232.098 186.503C231.426 187.134 230.552 187.506 229.631 187.552C227.747 187.65 225.859 187.51 224.01 187.136C200.335 183.798 201.704 183.862 201.428 184.002C201.336 184.028 201.255 184.081 201.194 184.154C201.133 184.227 201.096 184.317 201.087 184.411C201.078 184.506 201.097 184.601 201.143 184.684C201.188 184.767 201.258 184.835 201.342 184.878C208.568 191.046 216.108 196.834 223.934 202.219C221.02 202.934 218.072 203.498 215.1 203.909H215.089ZM266.611 189.251C268.564 192.434 269.413 196.173 269.025 199.887C268.639 203.602 267.038 207.085 264.471 209.797C260.279 213.784 254.214 213.59 248.916 212.497C243.484 211.302 238.248 209.347 233.362 206.69C222.092 200.619 213.353 193.456 203.294 185.185C205.679 185.532 217.941 187.21 221.671 187.811C223.8 188.155 225.942 188.463 228.098 188.611C229.409 188.716 230.726 188.476 231.916 187.914C232.621 187.499 233.16 186.85 233.435 186.077C233.712 185.306 233.709 184.462 233.426 183.693C232.676 181.822 231.388 180.216 229.725 179.077C222.716 173.004 212.095 167.743 203.423 164.486C208.639 163.276 214.078 163.426 219.22 164.919C233.96 169.82 247.389 174.769 259.953 183.468C262.495 184.994 264.752 186.951 266.622 189.251H266.611ZM261.331 151.097C264.668 151.087 267.988 151.576 271.181 152.547C273.873 153.446 276.262 155.076 278.082 157.254C283.442 163.643 283.307 172.397 280.309 176.815C277.825 180.474 272.773 181.86 268.587 183.01C266.733 183.521 264.866 183.984 262.992 184.416C251.871 175.846 237.237 169.994 223.878 165.426C221.153 164.346 218.333 163.525 215.454 162.974C211.152 162.436 206.789 162.665 202.567 163.651C203.195 162.96 203.94 162.388 204.77 161.958C205.97 161.336 207.207 160.789 208.475 160.319C223.578 154.328 239.658 151.178 255.905 151.03C257.709 151.013 259.518 151.069 261.331 151.097ZM233.099 134.272C235.429 132.228 238.196 130.744 241.188 129.933C244.13 129.293 247.188 129.446 250.051 130.376C252.914 131.307 255.478 132.981 257.481 135.228C258.501 136.405 259.274 137.775 259.752 139.257C260.231 140.739 260.406 142.302 260.267 143.853C260.119 145.974 259.649 148.06 258.873 150.04C244.655 149.849 230.499 151.943 216.945 156.24C212.572 157.581 208.298 159.226 204.153 161.162C214.827 147.364 223.927 141.425 233.095 134.272H233.099ZM72.3701 48.6866C73.587 47.9138 74.8039 49.181 75.5265 50.4268C75.8703 51.0215 76.2186 51.6132 76.538 52.2202C79.0133 56.8987 80.3535 62.0938 80.4504 67.3859C80.4512 68.3474 80.1681 69.2877 79.6366 70.0889C79.3464 70.3726 78.9918 70.5817 78.6031 70.6983C78.2143 70.8148 77.8032 70.8354 77.4048 70.7583C77.0064 70.6811 76.6326 70.5085 76.3155 70.2552C75.9985 70.0019 75.7476 69.6755 75.5843 69.304C74.5803 67.3676 70.0626 50.734 72.3701 48.6866ZM98.2933 146.862C92.8294 138.114 83.4926 133.078 75.1721 127.487C67.2074 122.134 59.048 115.738 55.0703 106.731C50.7198 96.8852 53.0822 85.6684 58.9081 76.9584C62.6122 71.6324 67.3249 67.0846 72.7793 63.5724C73.2937 65.6812 73.9374 67.7564 74.7066 69.7862C75.5797 71.8595 79.1179 72.8711 80.6284 70.4144C81.9365 68.3959 81.4635 65.5346 81.1501 63.3077C80.9508 61.8931 80.6649 60.4936 80.2952 59.1155C102.376 47.793 127.07 42.5433 151.848 43.9041C163.094 44.5369 174.612 46.7411 184.211 52.9336C188.68 55.6402 192.496 59.2991 195.388 63.6495C198.28 67.9999 200.177 72.9347 200.944 78.1022C203.834 99.6369 193.661 114.482 200.074 127.387C203.772 134.824 211.928 139.138 217.165 145.662C211.189 151.079 205.783 157.093 201.032 163.611C182.626 159.381 167.553 160.362 148.305 161.226C147.089 159.231 145.753 157.312 144.304 155.479C144.226 155.402 144.124 155.353 144.015 155.338C143.905 155.324 143.793 155.346 143.696 155.402C140.109 156.877 136.647 158.64 133.345 160.674C133.231 160.743 133.149 160.853 133.116 160.981C133.082 161.11 133.1 161.246 133.165 161.361L133.469 161.889L103.144 163.248C102.949 157.462 101.278 151.821 98.2917 146.862H98.2933ZM120.287 163.496V174.93C99.8336 184.156 80.0702 194.842 61.1502 206.904C60.6392 207.23 59.8375 207.496 59.8162 208.173C59.7949 208.85 59.8162 209.525 59.8162 210.199V250.909C57.9405 251.421 56.0434 251.852 54.1302 252.199C50.6073 253.051 46.9444 253.154 43.3789 252.503C40.0408 251.811 37.0929 249.871 35.1374 247.079C33.3196 244.348 32.1576 241.235 31.7422 237.981C30.0918 226.933 30.5816 204.61 33.7197 188.707C35.3199 178.88 39.9107 173.343 47.2228 169.845C50.9982 175.33 54.8954 180.738 58.1171 186.586C58.1806 186.682 58.2719 186.757 58.3787 186.799C58.459 186.83 58.5453 186.842 58.6308 186.833C58.7164 186.825 58.7989 186.798 58.872 186.752C58.9451 186.707 59.0068 186.646 59.0524 186.573C59.0979 186.5 59.1261 186.418 59.1348 186.332C59.6898 179.775 59.1334 173.17 57.4889 166.798C64.1606 165.853 61.3115 166.141 120.287 163.496ZM58.216 184.513C55.2528 179.153 51.8272 174.679 48.213 169.416C50.8513 168.302 53.6068 167.49 56.4271 166.994C57.8384 172.722 58.44 178.619 58.2145 184.513H58.216ZM15.4722 179.326C18.0115 178.373 20.8125 178.384 23.344 179.358C23.4157 179.376 23.4904 179.378 23.5629 179.364C23.6355 179.35 23.7041 179.321 23.7641 179.277C23.824 179.234 23.8738 179.179 23.91 179.114C23.9463 179.05 23.968 178.978 23.9738 178.905C23.981 178.806 23.9593 178.708 23.9114 178.622C21.0425 174.566 17.5105 169.23 19.926 164.134C22.4967 159.031 28.4794 156.702 32.4419 157.898C33.6264 158.222 34.6864 158.894 35.485 159.828C36.2835 160.761 36.784 161.912 36.9216 163.132C36.9642 163.772 37.9606 163.78 37.9256 163.132C37.1042 150.625 47.0296 143.096 55.6803 146.01C56.4545 146.27 57.1662 146.689 57.7701 147.238C58.374 147.788 58.8568 148.458 59.188 149.205C61.2065 154.529 58.0395 161.285 55.2665 166.218C52.0007 166.859 48.8345 167.93 45.8507 169.404C42.6767 170.969 39.9042 173.242 37.7465 176.048C35.5888 178.853 34.1035 182.116 33.4048 185.585C31.8673 192.669 30.8586 199.857 30.3869 207.09C24.4544 213.848 17.153 214.034 13.1661 210.252C12.1905 209.297 11.5151 208.078 11.2229 206.745C10.9307 205.411 11.0345 204.021 11.5215 202.746C12.0086 201.47 12.8576 200.365 13.9642 199.566C15.0709 198.766 16.387 198.307 17.7508 198.246C17.8713 198.245 17.9872 198.2 18.0775 198.12C18.1678 198.04 18.2264 197.931 18.2427 197.812C18.2589 197.692 18.2317 197.571 18.1659 197.47C18.1002 197.369 18.0004 197.295 17.8847 197.262C14.1579 196.188 9.21422 194.676 8.66661 190.167C8.48291 187.868 9.04518 185.571 10.27 183.617C11.4948 181.663 13.3169 180.155 15.4661 179.318L15.4722 179.326Z"
        fill="#021859"
      />
      <path
        d="M131.486 158.197C134.794 156.174 138.258 154.417 141.844 152.942C141.941 152.887 142.016 152.8 142.058 152.697C142.099 152.593 142.104 152.478 142.072 152.371C142.05 152.3 142.013 152.235 141.962 152.181L141.946 152.149C140.09 149.919 137.619 147.946 134.571 148.042C133.247 147.978 131.95 148.425 130.946 149.29C129.942 150.155 129.308 151.372 129.176 152.691C129.181 154.587 129.746 156.439 130.8 158.015C130.867 158.129 130.977 158.213 131.105 158.247C131.233 158.281 131.37 158.263 131.485 158.197H131.486ZM131.523 150.096C132.421 149.46 133.484 149.094 134.583 149.043C135.683 148.991 136.775 149.256 137.729 149.805C138.881 150.461 139.908 151.314 140.764 152.327C137.539 153.68 134.417 155.265 131.422 157.07C130.259 154.884 129.296 151.915 131.523 150.09V150.096Z"
        fill="#021859"
      />
      <path
        d="M185.977 238.915C185.711 238.327 184.846 238.837 185.11 239.421C185.755 240.84 186.399 242.264 187.042 243.689C187.308 244.277 188.172 243.767 187.909 243.183L185.977 238.915Z"
        fill="#021859"
      />
      <path
        d="M50.6697 163.025C52.0011 162.693 53.1894 161.939 54.0576 160.877C54.9258 159.814 55.4272 158.499 55.4872 157.129C55.5384 155.973 55.2247 154.831 54.5905 153.863C53.9563 152.896 53.0337 152.153 51.9536 151.739C50.9556 151.302 49.849 151.175 48.778 151.376C47.707 151.577 46.7214 152.096 45.9497 152.865C45.2692 153.686 44.7873 154.653 44.5414 155.691C44.2955 156.729 44.2923 157.81 44.532 158.849C44.833 160.195 45.6352 161.376 46.7756 162.151C47.916 162.927 49.3089 163.239 50.6713 163.025H50.6697ZM46.3071 154.042C46.8637 153.354 47.6066 152.84 48.4472 152.562C49.2878 152.284 50.1907 152.254 51.048 152.474C51.9054 152.695 52.6811 153.158 53.2827 153.808C53.8842 154.458 54.2862 155.267 54.4406 156.138C54.6266 157.45 54.3159 158.785 53.5694 159.88C52.8229 160.974 51.6944 161.751 50.4051 162.057C49.3876 162.202 48.3517 161.983 47.4793 161.44C46.607 160.897 45.9539 160.063 45.6348 159.086C45.3797 158.246 45.3078 157.362 45.4237 156.491C45.5397 155.621 45.841 154.786 46.3071 154.042Z"
        fill="#021859"
      />
      <path
        d="M228.256 261.566C228.384 261.559 228.504 261.503 228.592 261.409C228.658 261.34 228.703 261.253 228.722 261.159C228.74 261.065 228.731 260.968 228.696 260.878C228.66 260.789 228.6 260.712 228.522 260.656C228.444 260.601 228.352 260.568 228.256 260.563H135.109C135.014 260.568 134.921 260.6 134.843 260.656C134.765 260.712 134.704 260.789 134.669 260.878C134.633 260.967 134.624 261.064 134.642 261.159C134.66 261.253 134.705 261.34 134.772 261.409C134.86 261.503 134.981 261.559 135.109 261.566H228.256Z"
        fill="#021859"
      />
      <path
        d="M175.605 142.363C176.305 143.657 177.24 144.808 178.363 145.759C178.733 146.063 179.164 146.284 179.628 146.405C180.092 146.526 180.576 146.545 181.048 146.46C181.52 146.376 181.968 146.191 182.361 145.917C182.755 145.643 183.083 145.286 183.325 144.872C185.328 141.446 183.842 136.063 181.825 132.567C181.13 131.078 180.086 129.777 178.783 128.775C176.871 127.47 173.915 127.187 172.546 129.405C171.077 131.778 172.256 134.998 173.174 137.347C173.867 139.074 174.679 140.75 175.605 142.363ZM172.943 131.286C172.982 130.798 173.147 130.328 173.42 129.921C173.694 129.514 174.068 129.184 174.505 128.964C175.522 128.716 176.591 128.802 177.555 129.21C178.518 129.618 179.325 130.325 179.855 131.227C180.958 132.845 181.84 134.603 182.479 136.454C183.245 138.49 183.431 140.698 183.018 142.833C182.757 145.133 180.605 146.282 178.869 144.891C178.039 144.178 177.336 143.327 176.792 142.377C175.29 139.958 174.119 137.349 173.308 134.619C172.961 133.545 172.837 132.411 172.943 131.286Z"
        fill="#021859"
      />
      <path
        d="M145.93 123.14C146.301 123.018 146.645 122.826 146.947 122.578C148.45 121.361 148.126 118.979 147.9 117.27C147.314 113.1 146.401 108.984 145.171 104.958C144.695 103.255 143.983 101.628 143.055 100.123C142.384 99.1848 141.284 98.4045 140.245 99.34C139.343 100.332 138.853 101.631 138.876 102.972C138.757 107.349 139.261 111.721 140.373 115.955C141.093 118.975 142.986 124.08 145.93 123.14ZM140.238 101.155C140.962 99.5712 141.619 99.2122 142.919 101.895C144.964 106.171 146.155 113 146.722 116.127C146.954 117.314 147.068 118.52 147.063 119.729C147.121 121.088 146.159 122.552 144.842 122.187C143.374 121.781 142.052 118.31 141.648 116.822C140.483 112.758 139.869 108.556 139.822 104.328C139.777 103.254 139.918 102.181 140.238 101.155Z"
        fill="#021859"
      />
      <path
        d="M148.689 73.5724C149.194 73.8457 149.785 73.916 150.341 73.7691C150.896 73.6221 151.375 73.2689 151.679 72.7814C152.079 72.105 152.336 71.3542 152.435 70.5751C152.534 69.7959 152.473 69.0047 152.256 68.2499C151.545 65.0753 151.211 57.2308 147.093 57.6141C145.706 57.7434 145.199 58.9056 145.1 60.1499C144.875 63.7869 145.572 71.8839 148.689 73.5724ZM146.077 60.8633C146.077 60.0662 146.066 58.7672 147.093 58.6181C150.287 58.1618 150.592 65.7674 151.288 68.5176C151.6 69.6828 151.492 70.921 150.984 72.0162C150.856 72.3354 150.607 72.5917 150.293 72.7307C149.978 72.8696 149.621 72.8802 149.299 72.7601C148.237 72.0425 147.495 70.9409 147.23 69.6874C146.35 66.8322 145.961 63.8486 146.077 60.8633Z"
        fill="#021859"
      />
      <path
        d="M169.692 112.977C170.354 112.775 170.658 112.002 170.825 111.405C171.344 109.543 171.154 105.624 169.283 106.135C167.001 107.027 166.87 110.381 168.399 112.466C168.54 112.669 168.738 112.826 168.968 112.917C169.198 113.008 169.45 113.029 169.692 112.977ZM168.965 107.576C169.56 106.891 169.725 106.943 169.929 107.822C170.139 108.948 170.106 110.104 169.833 111.214C169.566 112.15 169.307 112.307 168.825 111.266C168.538 110.637 168.394 109.953 168.403 109.261C168.381 108.957 168.419 108.652 168.516 108.363C168.612 108.073 168.765 107.806 168.965 107.576Z"
        fill="#021859"
      />
      <path
        d="M153.473 239.662C151.823 238.599 149.969 237.892 148.029 237.586C146.948 237.434 143.502 236.987 143.706 238.946C143.872 240.532 146.733 241.083 148.311 241.603C153.619 243.359 154.313 243.918 158.238 245.292C158.747 245.47 159.026 244.801 158.724 244.454C157.219 242.607 155.45 240.992 153.473 239.662ZM148.268 240.535C147.414 240.289 146.569 240.01 145.735 239.7C145.396 239.571 145.092 239.362 144.849 239.092C144.566 238.769 144.791 238.635 145.132 238.549C147.892 238.296 150.656 238.994 152.967 240.526C154.31 241.408 155.539 242.452 156.627 243.634C154.919 242.984 150.605 241.237 148.268 240.535Z"
        fill="#021859"
      />
      <path
        d="M157.918 194.75C159.539 194.887 161.579 196.17 161.24 198.06C161.128 198.686 162.109 198.972 162.208 198.326C162.322 197.496 162.162 196.652 161.753 195.921C161.344 195.19 160.708 194.613 159.941 194.277C158.453 193.653 156.8 193.543 155.242 193.963C155.247 193.919 155.247 193.875 155.242 193.831C154.698 190.825 152.974 187.453 149.651 187.01C148.647 186.896 147.634 186.875 146.627 186.948C143.463 187.066 140.311 187.386 137.188 187.908C134.821 188.271 132.477 188.775 130.169 189.415C130.077 189.444 129.994 189.499 129.932 189.574C129.87 189.648 129.831 189.74 129.82 189.836C129.809 189.932 129.826 190.03 129.869 190.117C129.912 190.204 129.979 190.277 130.063 190.326C130.175 190.393 130.309 190.413 130.436 190.382C136.213 188.845 142.155 188.016 148.132 187.914C148.918 187.844 149.71 187.963 150.44 188.26C151.171 188.557 151.821 189.024 152.335 189.622C153.37 190.944 154.048 192.51 154.304 194.17C147.769 195.954 141.43 198.388 135.381 201.437C134.798 201.71 135.305 202.574 135.887 202.302C141.158 199.696 146.626 197.509 152.24 195.761C154.057 195.088 155.98 194.745 157.918 194.75Z"
        fill="#021859"
      />
      <path
        d="M57.0937 209.259C53.3054 211.624 49.425 213.839 45.4616 215.898C44.8897 216.203 45.3962 217.062 45.9682 216.766C49.9315 214.706 53.8119 212.491 57.6003 210.126C58.1463 209.784 57.6428 208.915 57.0937 209.259Z"
        fill="#021859"
      />
      <path
        d="M43.7719 245.324C42.4403 244.336 41.2217 243.204 40.1379 241.949C39.715 241.461 39.0077 242.173 39.429 242.658C40.5736 243.974 41.8602 245.159 43.2653 246.192C43.784 246.575 44.286 245.705 43.7719 245.324Z"
        fill="#021859"
      />
      <path
        d="M295.991 149.047L283.323 151.128C282.687 151.231 282.956 152.192 283.589 152.095L296.258 150.014C296.893 149.911 296.621 148.943 295.991 149.047Z"
        fill="#021859"
      />
      <path
        d="M292.034 208.198L281.043 200.471C280.512 200.098 280.011 200.968 280.536 201.336L291.528 209.065C292.057 209.438 292.558 208.568 292.034 208.198Z"
        fill="#021859"
      />
      <path
        d="M255.094 239.473C255.216 239.435 255.318 239.353 255.381 239.242C255.444 239.131 255.463 239.001 255.433 238.877C253.924 233.303 252.142 226.653 251.968 226.063C251.94 225.97 251.885 225.887 251.81 225.824C251.735 225.762 251.644 225.723 251.547 225.711C251.451 225.7 251.353 225.717 251.266 225.761C251.179 225.804 251.106 225.872 251.057 225.956C250.99 226.069 250.97 226.202 251.001 226.329C252.011 230.063 254.309 238.609 254.464 239.143C254.504 239.27 254.593 239.376 254.711 239.438C254.829 239.5 254.967 239.513 255.094 239.473Z"
        fill="#021859"
      />
      <path
        d="M82.5089 191.543C82.7112 192.151 83.6802 191.892 83.4779 191.276L81.432 185.087C81.2312 184.478 80.2607 184.737 80.466 185.353C81.146 187.417 81.8259 189.481 82.5089 191.543Z"
        fill="#021859"
      />
      <path
        d="M272.603 115.849L264.773 124.594C264.343 125.073 265.05 125.785 265.482 125.303L273.311 116.559C273.742 116.079 273.035 115.367 272.603 115.849Z"
        fill="#021859"
      />
      <path
        d="M223.012 112.107C222.903 112.175 222.825 112.281 222.793 112.404C222.761 112.528 222.777 112.659 222.838 112.771C224.533 115.766 225.823 117.927 228.844 122.856C228.913 122.964 229.023 123.042 229.148 123.071C229.274 123.101 229.406 123.081 229.518 123.015C229.63 122.951 229.712 122.845 229.747 122.72C229.782 122.596 229.768 122.464 229.709 122.349L223.705 112.264C223.67 112.208 223.624 112.16 223.57 112.121C223.516 112.083 223.455 112.056 223.391 112.042C223.327 112.027 223.26 112.025 223.195 112.037C223.13 112.048 223.067 112.072 223.012 112.107Z"
        fill="#021859"
      />
    </svg>
    <div className="font-medium text-lg leading-none tracking-[0%] text-center text-brand-primary-new">
      Blank Slate, Bold Fate
    </div>
    <div className="font-normal text-base leading-none tracking-[0%] text-center text-[#617390] mt-2">
      Create something new, let your audience cheer.
    </div>
  </div>
);

export default CreateDashboard;
